import { Grid, Modal, Paper, Stack } from "@mui/material";
import * as Comlink from "comlink";
import { IconButton } from "components/IconButton";
import ReactECharts from "echarts-for-react";
import { isEqual, reverse } from "lodash";
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BsFileText } from "react-icons/bs";
import { FaMapLocationDot } from "react-icons/fa6";
import { GiHistogram } from "react-icons/gi";
import { IoBarChartOutline, IoPieChartOutline } from "react-icons/io5";
import { LiaChartAreaSolid } from "react-icons/lia";
import { PiChartDonutThin, PiChartLineLight } from "react-icons/pi";
import { PiChartScatter } from "react-icons/pi";
import { TbChartHistogram, TbSum } from "react-icons/tb";
import { TfiLayoutGrid4Alt } from "react-icons/tfi";
import { useDispatch, useSelector } from "react-redux";
import { Legend, ResponsiveContainer, Tooltip } from "recharts";
import { setDataToDownload } from "store/slices/energyPerformance/energyPerformanceSliceNew";
import { plus } from "utils/textSymbols";

import { Button } from "../../components/ButtonNew";
import { chartTypesEnums, diagramTypes, valuesEnums } from "../../enums/dynamicDashboard";
import { useDelayedTransitionedDisplay } from "../../hooks/components";
import { distinctFilter, findDataRange, isNumeric, keepKeysFromObjects, max } from "../../utils/dataManipulation";
import {
  aggregatedSumValue,
  aggregationFunctionConfig,
  defaultSortFunction,
  diagramInitialConfig,
  echartsDefaultTooltipFormatter,
  generateAxisValues,
  getArrayLongestElement,
  getAxisNameWithUnit,
  getDataKeyFromDiagramFilterName,
  getItemColor,
  getSeriesMixedName,
  hasSecondYAxis,
  pivotData,
  sortData,
  sortDataAccumulative,
} from "../../utils/dynamicDashboard";
import {
  getPieAndDoughnutOptions,
  getScatterChartOptions,
  useGetScatterChartAxisOption,
  useScatterChartDataDownload,
} from "../../utils/dynamicDashboardCharts";
import { formatNumberBasedOnUser } from "../../utils/namesManipulation";
import { cn, getCSSVar } from "../../utils/styling";
import { CaptureComponent } from "../CaptureComponent";
import CSVLink from "../CSVLink";
import { DynamicFilterFactory } from "../DynamicFormInput";
import { EmptyErrorBoundary, TextErrorBoundary } from "../ErrorBoundary";
import { Icon } from "../Icon";
import { LoadingOrEmptyWrapper } from "../LoadingAndEmptyHandler";
import { MyMap } from "../map/Map";
import { ChartCategoricalIndividualFilters, ChartIndividualFiltersModal } from "./DynamicChartFilters";
import { DynamicChartTitle } from "./DynamicChartTitle";
import { createMiniMapCategoricalStrategy, createMiniMapNumericalStrategy } from "./DynamicMiniMapStrategy";
import LegendItem from "./LegendItem";
// import JSONfn from 'json-fn';

const useDynamicDashboardCalculations = () => {
  const [isInTransformingAndAggregating, setIsInTransformingAndAggregating] = useState(false);
  const [isAggregating, setIsAggregating] = useState(false);
  const [isTransforming, setIsTransforming] = useState(false);
  const [updatedOption, setUpdatedOption] = useState({});
  const [isPainting, setIsPainting] = useState(false);

  const isProcessing = isInTransformingAndAggregating || isAggregating || isTransforming;

  const dataTransformatorDecorator = async (dataTransformator) => {
    try {
      setIsInTransformingAndAggregating(true);
      setIsPainting(false);
      setIsTransforming(true);
      await dataTransformator();
    } finally {
      setIsTransforming(false);
    }
  };

  const dataAggregatorDecorator = async (dataAggregater, worker) => {
    try {
      setIsPainting(false);
      setIsAggregating(true);
      await dataAggregater();
    } finally {
      setIsAggregating(false);
      worker.terminate();
    }
  };

  const setOption = (newOptions) => {
    if (!isAggregating && !isTransforming) {
      setIsPainting(true);
      setIsInTransformingAndAggregating(false);
      setUpdatedOption(newOptions);
    }
  };

  return {
    dataAggregatorDecorator,
    dataTransformatorDecorator,
    isAggregating,
    isPainting,
    isProcessing,
    isTransforming,
    option: updatedOption,
    setIsAggregating,
    setIsPainting,
    setIsProcessing: setIsInTransformingAndAggregating,
    setIsTransforming,
    setOption,
  };
};

const DynamicComboChartMultithread = ({ isConfigSetting, xDataKey, ...props }) => {
  const dispatch = useDispatch();
  const {
    chartsConfigs,
    config,
    data: withoutIndexData,
    dataTransformatorMultithread,
    description,
    getColor,
    getColumnDisplayName,
    getColumnSortFunction,
    getDataTransformatorMemoDependencyArray,
    getUnit,
    hoverEffect,
    id,
    isFullScreen,
    isItemHovered,
    legendsPosition,
    onClick,
    onHover,
    onLegendSelect,
    onMouseOut,
    secondYAxisName: secondYAxisNameConfig,
    secondYAxisRange,
    somethingIsHovered,
    sort,
    title,
    xAxisName: xAxisNameConfig,
    xAxisRange,
    yAxisName: yAxisNameConfig,
    yAxisRange,
  } = props;
  const aggregationFunction = props.aggregationFunction ?? aggregatedSumValue;

  const { aggregateXAxis, diagrams, invertXAxis, invertYAxis } = config;
  const selectedConfigs = chartsConfigs?.filter((config) => config.dataKey && config.diagramType);

  const diagramsFiltersNames = Object.keys(diagrams?.[0] ?? {})?.filter((key) =>
    key.startsWith(valuesEnums.DIAGRAM_FILTER_PREFIX)
  );
  const diagramsActiveFiltersNames = diagramsFiltersNames?.filter((col) =>
    selectedConfigs?.some((conf) => conf[col] !== undefined)
  );

  const diagramsFiltersDataKeys = diagramsActiveFiltersNames?.map((filterName) =>
    getDataKeyFromDiagramFilterName({ filterName })
  );
  const diagramsSegments = diagramsFiltersDataKeys?.filter(distinctFilter) || [];

  const dataTransformatorDependency = getDataTransformatorMemoDependencyArray({
    config,
  });

  const [flattenData, setFlattenData] = useState([]);

  const {
    dataAggregatorDecorator,
    dataTransformatorDecorator,
    isPainting,
    isProcessing,
    option,
    setIsPainting,
    setOption,
  } = useDynamicDashboardCalculations();

  const activeWorkerRef = useRef(null);
  const workerId = useRef(0);
  useEffect(() => {
    if (activeWorkerRef.current?.terminate) {
      activeWorkerRef.current.terminate();
    }

    workerId.current += 1;

    const workerInstance = dataTransformatorMultithread();
    activeWorkerRef.current = workerInstance;
    const getTransformedData = async () => {
      const { flattenData: newFlattenData, requestId } = await workerInstance.calculate(
        withoutIndexData,
        { ...config },
        workerId.current
      );
      if (requestId === workerId.current) {
        setFlattenData(newFlattenData);
      }
    };

    if (!withoutIndexData || isConfigSetting) return;
    dataTransformatorDecorator(getTransformedData);

    return () => {
      if (workerInstance.terminate) {
        workerInstance.terminate();
      }
    };
  }, [JSON.stringify(dataTransformatorDependency), withoutIndexData, isConfigSetting]);

  const xIsNumeric = flattenData?.some((row) => isNumeric(row[xDataKey]));

  const aggregate = aggregateXAxis === undefined ? true : aggregateXAxis; // Keep this line for backward compatibility

  const [allAggregatedData, SetAllAggregatedData] = useState([]);
  const [sortedData, setSortedData] = useState([]);
  const [xAxisValues, SetXAxisValues] = useState([]);
  const [data, setData] = useState([]);

  const sortAggregatedDataOnX = (aggregatedData) => {
    const sorted = aggregatedData?.toSorted((a, b) =>
      getColumnSortFunction({
        aValue: a[xDataKey],
        bValue: b[xDataKey],
        dataKey: xDataKey,
      })
    );
    return sorted;
  };

  const activeAggregaterWorkerRef = useRef(null);
  const aggregaterWorkerId = useRef(0);
  useEffect(
    () => {
      if (activeAggregaterWorkerRef.current) {
        activeAggregaterWorkerRef.current?.terminate();
      }

      aggregaterWorkerId.current += 1;

      const worker = new Worker(new URL("../../../src/comboChart.worker.js", import.meta.url));
      activeAggregaterWorkerRef.current = worker;

      const runWorker = async (...props) => {
        const comboChartDataAggregater = Comlink.wrap(worker);
        const result = await comboChartDataAggregater(...props);
        return result;
      };

      const aggregater = async () => {
        let {
          aggregatedData,
          data: dataTemp,
          requestId,
          sortedData: sortedDataTemp,
          xAxisValues: xAxisValuesTemp,
        } = await runWorker(
          {
            aggregate,
            aggregateColumns: selectedConfigs?.map((config) => config.dataKey)?.filter(distinctFilter),
            dataKeys: selectedConfigs?.map((config) => config.dataKey),
            flattenData,
            groupByColumns: [xDataKey, ...diagramsSegments],
            sort,
            xDataKey,
            xIsNumeric,
          },
          getColumnSortFunction ? Comlink.proxy(sortAggregatedDataOnX) : undefined,
          aggregaterWorkerId.current,
          Comlink.proxy(aggregationFunction)
        );

        if (requestId === aggregaterWorkerId.current) {
          SetAllAggregatedData(aggregatedData);
          setSortedData(sortedDataTemp);
          SetXAxisValues(xAxisValuesTemp);
          setData(dataTemp);
        }
      };

      if (!flattenData || isConfigSetting) return;
      dataAggregatorDecorator(aggregater, worker);

      return () => {
        worker.terminate();
      };
    },
    [flattenData, xDataKey, sort, JSON.stringify(selectedConfigs), isConfigSetting, props.aggregationFunction]
    // [partitions, stacks, sort, flattenData]
  );

  const xIsCategory = selectedConfigs?.some((selectedConfig) => selectedConfig.diagramType === diagramTypes.BAR);

  let xAxis = xIsNumeric
    ? {
        inverse: invertXAxis,
        type: xIsCategory ? "category" : "value",
      }
    : {
        axisPointer: {
          type: "shadow",
        },
        data: xAxisValues,
        inverse: invertXAxis,
      };

  const xUnit = getUnit({ config, quantity: xDataKey });
  const xAxisName =
    xAxisNameConfig ||
    getAxisNameWithUnit({
      dataKey: getColumnDisplayName({ colName: xDataKey }),
      unit: xUnit,
    });
  xAxis = [{ ...xAxis, name: xAxisName, nameGap: 25, nameLocation: "center" }];

  let yAxis = xIsNumeric
    ? [
        {
          inverse: invertYAxis,
        },
      ]
    : [
        {
          inverse: invertYAxis,
          type: "value",
        },
      ];

  const mainYAxisUnits = selectedConfigs
    ?.filter((config) => !config.asSecondYAxis)
    .map((config) =>
      getAxisNameWithUnit({
        unit: getUnit({ config, quantity: config.dataKey }),
      })
    );
  const mainYAxisUnitUnique = mainYAxisUnits.filter(distinctFilter);
  const mainYAxisUnitText = mainYAxisUnitUnique.join(" - ");

  const secondYAxisUnits = selectedConfigs
    ?.filter((config) => config.asSecondYAxis)
    .map((config) =>
      getAxisNameWithUnit({
        unit: getUnit({ config, quantity: config.dataKey }),
      })
    );
  const secondYAxisUnitUnique = secondYAxisUnits.filter(distinctFilter);
  const secondYAxisUnitText = secondYAxisUnitUnique.join(" - ");

  yAxis = [{ ...yAxis[0], name: yAxisNameConfig || mainYAxisUnitText }];
  if (hasSecondYAxis({ diagrams })) yAxis.push({ ...yAxis[0], name: secondYAxisNameConfig || secondYAxisUnitText });

  useEffect(() => {
    if (isConfigSetting || !flattenData) return;
    const newOptions = {
      series: selectedConfigs?.map((selectedConfig, i) => {
        const activeFiltersNames = diagramsFiltersNames?.filter((col) => selectedConfig[col] !== undefined);
        const activeFiltersNamesAndValues = activeFiltersNames?.map((col) => [col, selectedConfig[col]]);

        const seriesMixedName = getSeriesMixedName({
          filters: activeFiltersNamesAndValues,
          seriesName: selectedConfig.dataKey,
        });
        const color =
          getColor &&
          getColor({
            chartType: config?.chartType,
            dataPoint: selectedConfig.dataKey,
            diagrams,
            quantity: seriesMixedName,
          });
        const seriesRelatedData = sortedData?.filter((row) => {
          let output = true;
          activeFiltersNames?.forEach((filterName, index) => {
            const selectedValue = selectedConfig[filterName];

            const dataColName = diagramsFiltersDataKeys[index];
            const rowValue = row[dataColName];
            if (selectedValue !== rowValue) output = false;
          });
          return output;
        });
        let seriesAggregatedData = !aggregate
          ? seriesRelatedData
          : aggregationFunction({
              aggregateColumns: [selectedConfig.dataKey],
              data: seriesRelatedData,
              groupByColumns: [xDataKey],
            });

        return {
          areaStyle: selectedConfig.diagramType === chartTypesEnums.AREA ? {} : undefined,
          color: color ?? getItemColor(i),
          // data: xIsNumeric ? sortedData?.map((row, index) => ({
          //   value: [row[xDataKey], row[selectedConfig?.dataKey]],
          //   itemStyle: {
          //     opacity: (somethingIsHovered && isItemHovered && !isItemHovered({
          //       row, selectedConfig, chartType: config?.chartType
          //       , xAxis: config?.xAxis, yAxis: config?.yAxis, diagrams
          //     })) ? 0.05 : 1
          //   },
          //   row
          // })) : sortedData?.map((row) => ({
          //   value: row[selectedConfig?.dataKey],
          //   itemStyle: {
          //     opacity: (somethingIsHovered && isItemHovered && !isItemHovered({
          //       row, selectedConfig, chartType: config?.chartType
          //       , xAxis: config?.xAxis, yAxis: config?.yAxis, diagrams
          //     })) ? 0.05 : 1
          //   },
          //   row
          // }))
          data: seriesAggregatedData?.map((row) => ({
            itemStyle: {
              opacity:
                somethingIsHovered &&
                isItemHovered &&
                !isItemHovered({
                  chartType: config?.chartType,
                  diagrams,
                  row,
                  selectedConfig,
                  xAxis: config?.xAxis,
                  yAxis: config?.yAxis,
                })
                  ? 0.05
                  : 1,
            },
            row,
            value: [row[xDataKey], row[selectedConfig?.dataKey]],
          })),
          emphasis: {
            focus: hoverEffect ? undefined : "series",
          },
          large: true,
          largeThreshold: 1000,
          maxValue: seriesAggregatedData?.length
            ? Math.max(...seriesAggregatedData.map((row) => row[selectedConfig?.dataKey] ?? 0))
            : 0,
          name: seriesMixedName,
          progressive: 500,
          progressiveThreshold: 1000,
          stack: selectedConfig.stackId,
          tooltip: {
            trigger: "item",
            valueFormatter: echartsDefaultTooltipFormatter,
          },
          type: selectedConfig.diagramType === chartTypesEnums.AREA ? chartTypesEnums.LINE : selectedConfig.diagramType,
          yAxisIndex: selectedConfig.asSecondYAxis ? 1 : 0,
        };
      }),
    };
    setOption({ ...newOptions });
  }, [
    somethingIsHovered,
    allAggregatedData,
    JSON.stringify(selectedConfigs),
    diagrams,
    isItemHovered,
    config?.chartType,
    xDataKey,
    config.xAxis,
    config?.yAxis,
    sortedData,
    getColor,
    isConfigSetting,
    props.aggregationFunction,
  ]);

  const optionWithAxis = { ...option, xAxis, yAxis };

  const dataKeysForDownload = selectedConfigs?.map((config) => config.dataKey);
  useEffect(() => {
    try {
      if (dataKeysForDownload) {
        dataKeysForDownload?.push(xDataKey);
        let dataToDownload = keepKeysFromObjects(xIsNumeric ? data : allAggregatedData, dataKeysForDownload);
        dispatch(setDataToDownload({ data: dataToDownload, id: id }));
      }
    } catch {
      //Do nothing
    }
  }, [JSON.stringify(dataKeysForDownload), data, allAggregatedData, id]);

  const longestY = getArrayLongestElement(optionWithAxis.series?.map((s) => s.maxValue ?? 0));

  if (isConfigSetting) return;
  return (
    <DynamicEchartsChartWithLoader
      getLegendDisplayName={(colName) => getColumnDisplayName({ colName })}
      isFullScreen={isFullScreen}
      isPainting={isPainting}
      isProcessing={isProcessing}
      legendsPosition={legendsPosition}
      longestY={longestY}
      onClick={onClick ? (params) => onClick({ config, params, point: params?.data?.row }) : undefined}
      onFinished={() => {
        setIsPainting(false);
      }}
      onHover={onHover ? (params) => onHover({ config, params, point: params?.data?.row }) : undefined}
      onLegendSelect={onLegendSelect ? (params) => onLegendSelect({ config, params }) : undefined}
      onMouseOut={onMouseOut ? (params) => onMouseOut({ config, params }) : undefined}
      option={optionWithAxis}
      setIsPainting={setIsPainting}
      title={title}
      tooltip={description}
      {...{ secondYAxisRange, xAxisRange, yAxisRange }}
    />
  );
};

const DynamicComboChart = ({ xDataKey, ...props }) => {
  const dispatch = useDispatch();
  const {
    chartsConfigs,
    config,
    data: withoutIndexData,
    dataTransformator,
    description,
    getColor,
    getColumnDisplayName,
    getColumnSortFunction,
    getDataTransformatorMemoDependencyArray,
    getUnit,
    hoverEffect,
    id,
    isFullScreen,
    isItemHovered,
    legendsPosition,
    onClick,
    onHover,
    onLegendSelect,
    onMouseOut,
    secondYAxisName: secondYAxisNameConfig,
    secondYAxisRange,
    somethingIsHovered,
    sort,
    title,
    xAxisName: xAxisNameConfig,
    xAxisRange,
    yAxisName: yAxisNameConfig,
    yAxisRange,
  } = props;
  const aggregationFunction = props.aggregationFunction ?? aggregatedSumValue;

  const { aggregateXAxis, diagrams, invertXAxis, invertYAxis } = config;
  const selectedConfigs = chartsConfigs?.filter((config) => config.dataKey && config.diagramType);

  const diagramsFiltersNames = Object.keys(diagrams?.[0] ?? {})?.filter((key) =>
    key.startsWith(valuesEnums.DIAGRAM_FILTER_PREFIX)
  );
  const diagramsActiveFiltersNames = diagramsFiltersNames?.filter((col) =>
    selectedConfigs?.some((conf) => conf[col] !== undefined)
  );

  const diagramsFiltersDataKeys = diagramsActiveFiltersNames?.map((filterName) =>
    getDataKeyFromDiagramFilterName({ filterName })
  );
  const diagramsSegments = diagramsFiltersDataKeys?.filter(distinctFilter) || [];

  const dataTransformatorDependency = getDataTransformatorMemoDependencyArray({
    config,
  });
  let { flattenData } = useMemo(
    () => dataTransformator(withoutIndexData, { ...config }),
    [dataTransformatorDependency, withoutIndexData]
  );

  const xIsNumeric = flattenData?.some((row) => isNumeric(row[xDataKey]));

  const aggregate = aggregateXAxis === undefined ? true : aggregateXAxis; // Keep this line for backward compatibility
  const { aggregatedData, data, sortedData } = useMemo(() => {
    if (!flattenData?.length || !xDataKey) return {};

    const data = flattenData?.map((d, index) => ({
      ...d,
      index,
    }));

    let aggregatedData = !aggregate
      ? data
      : aggregationFunction({
          aggregateColumns: selectedConfigs?.map((config) => config.dataKey)?.filter(distinctFilter),
          data,
          groupByColumns: [xDataKey, ...diagramsSegments],
        });

    if (sort === "y") {
      aggregatedData = sortDataAccumulative({
        data: aggregatedData,
        dataKeys: selectedConfigs?.map((config) => config.dataKey),
      });
    } else {
      if (getColumnSortFunction)
        aggregatedData.sort((a, b) =>
          getColumnSortFunction({
            aValue: a[xDataKey],
            bValue: b[xDataKey],
            dataKey: xDataKey,
          })
        );
      else aggregatedData = sortData({ data: aggregatedData, dataKey: xDataKey });
    }

    // Remove hardcoded value
    const sortedData =
      xDataKey !== "index" || !sort ? aggregatedData : aggregatedData?.map((d, index) => ({ ...d, index }));

    return { aggregatedData, data, sortedData };
  }, [flattenData, xDataKey, sort, JSON.stringify(selectedConfigs), props.aggregationFunction]);

  const xAxisValues = useMemo(() => {
    return !xIsNumeric && sortedData?.map((row) => row[xDataKey]).filter(distinctFilter);
  }, [xDataKey, sortedData]);

  const xIsCategory = selectedConfigs?.some((selectedConfig) => selectedConfig.diagramType === diagramTypes.BAR);

  let xAxis = xIsNumeric
    ? {
        inverse: invertXAxis,
        type: xIsCategory ? "category" : "value",
      }
    : {
        axisPointer: {
          type: "shadow",
        },
        data: xAxisValues,
        inverse: invertXAxis,
      };

  const xUnit = getUnit({ config, quantity: xDataKey });
  const xAxisName =
    xAxisNameConfig ||
    getAxisNameWithUnit({
      dataKey: getColumnDisplayName({ colName: xDataKey }),
      unit: xUnit,
    });
  xAxis = [{ ...xAxis, name: xAxisName, nameGap: 25, nameLocation: "center" }];

  let yAxis = xIsNumeric
    ? [
        {
          inverse: invertYAxis,
        },
      ]
    : [
        {
          inverse: invertYAxis,
          type: "value",
        },
      ];

  const mainYAxisUnits = selectedConfigs
    ?.filter((config) => !config.asSecondYAxis)
    .map((config) =>
      getAxisNameWithUnit({
        unit: getUnit({ config, quantity: config.dataKey }),
      })
    );
  const mainYAxisUnitUnique = mainYAxisUnits.filter(distinctFilter);
  const mainYAxisUnitText = mainYAxisUnitUnique.join(" - ");

  const secondYAxisUnits = selectedConfigs
    ?.filter((config) => config.asSecondYAxis)
    .map((config) =>
      getAxisNameWithUnit({
        unit: getUnit({ config, quantity: config.dataKey }),
      })
    );
  const secondYAxisUnitUnique = secondYAxisUnits.filter(distinctFilter);
  const secondYAxisUnitText = secondYAxisUnitUnique.join(" - ");

  yAxis = [{ ...yAxis[0], name: yAxisNameConfig || mainYAxisUnitText }];

  if (hasSecondYAxis({ diagrams })) yAxis.push({ ...yAxis[0], name: secondYAxisNameConfig || secondYAxisUnitText });

  const option = useMemo(() => {
    return {
      series: selectedConfigs?.map((selectedConfig, i) => {
        const activeFiltersNames = diagramsFiltersNames?.filter((col) => selectedConfig[col] !== undefined);
        const activeFiltersNamesAndValues = activeFiltersNames?.map((col) => [col, selectedConfig[col]]);

        const seriesMixedName = getSeriesMixedName({
          filters: activeFiltersNamesAndValues,
          seriesName: selectedConfig.dataKey,
        });
        const color =
          getColor &&
          getColor({
            chartType: config?.chartType,
            dataPoint: selectedConfig.dataKey,
            diagrams,
            quantity: seriesMixedName,
          });
        const seriesRelatedData = sortedData?.filter((row) => {
          let output = true;
          activeFiltersNames?.forEach((filterName, index) => {
            const selectedValue = selectedConfig[filterName];

            const dataColName = diagramsFiltersDataKeys[index];
            const rowValue = row[dataColName];
            if (selectedValue !== rowValue) output = false;
          });
          return output;
        });
        let seriesAggregatedData = !aggregate
          ? seriesRelatedData
          : aggregationFunction({
              aggregateColumns: [selectedConfig.dataKey],
              data: seriesRelatedData,
              groupByColumns: [xDataKey],
            });

        return {
          areaStyle: selectedConfig.diagramType === chartTypesEnums.AREA ? {} : undefined,
          color: color ?? getItemColor(i),
          // data: xIsNumeric ? sortedData?.map((row, index) => ({
          //   value: [row[xDataKey], row[selectedConfig?.dataKey]],
          //   itemStyle: {
          //     opacity: (somethingIsHovered && isItemHovered && !isItemHovered({
          //       row, selectedConfig, chartType: config?.chartType
          //       , xAxis: config?.xAxis, yAxis: config?.yAxis, diagrams
          //     })) ? 0.05 : 1
          //   },
          //   row
          // })) : sortedData?.map((row) => ({
          //   value: row[selectedConfig?.dataKey],
          //   itemStyle: {
          //     opacity: (somethingIsHovered && isItemHovered && !isItemHovered({
          //       row, selectedConfig, chartType: config?.chartType
          //       , xAxis: config?.xAxis, yAxis: config?.yAxis, diagrams
          //     })) ? 0.05 : 1
          //   },
          //   row
          // }))
          data: seriesAggregatedData?.map((row) => ({
            itemStyle: {
              opacity:
                somethingIsHovered &&
                isItemHovered &&
                !isItemHovered({
                  chartType: config?.chartType,
                  diagrams,
                  row,
                  selectedConfig,
                  xAxis: config?.xAxis,
                  yAxis: config?.yAxis,
                })
                  ? 0.05
                  : 1,
            },
            row,
            value: [row[xDataKey], row[selectedConfig?.dataKey]],
          })),
          emphasis: {
            focus: hoverEffect ? undefined : "series",
          },
          maxValue: seriesAggregatedData?.length
            ? Math.max(...seriesAggregatedData.map((row) => row[selectedConfig?.dataKey] ?? 0))
            : 0,
          name: seriesMixedName,
          stack: selectedConfig.stackId,
          tooltip: {
            trigger: "item",
            valueFormatter: echartsDefaultTooltipFormatter,
          },
          type: selectedConfig.diagramType === chartTypesEnums.AREA ? chartTypesEnums.LINE : selectedConfig.diagramType,
          yAxisIndex: selectedConfig.asSecondYAxis ? 1 : 0,
        };
      }),
    };
  }, [
    somethingIsHovered,
    isItemHovered,
    JSON.stringify(selectedConfigs),
    config?.xAxis,
    diagrams,
    sortedData,
    config,
    getColor,
    config?.chartType,
    xDataKey,
    config?.yAxis,
    props.aggregationFunction,
  ]);

  const optionWithAxis = { ...option, xAxis, yAxis };

  const dataKeysForDownload = selectedConfigs?.map((config) => config.dataKey);

  useEffect(() => {
    if (dataKeysForDownload) {
      dataKeysForDownload?.push(xDataKey);
      let dataToDownload = keepKeysFromObjects(xIsNumeric ? data : aggregatedData, dataKeysForDownload);
      dispatch(setDataToDownload({ data: dataToDownload, id: id }));
    }
  }, [JSON.stringify(dataKeysForDownload), data, aggregatedData, id]);

  const longestY = getArrayLongestElement(optionWithAxis.series?.map((s) => s.maxValue ?? 0));
  return (
    <DynamicEchartsChart
      getLegendDisplayName={(colName) => getColumnDisplayName({ colName })}
      isFullScreen={isFullScreen}
      legendsPosition={legendsPosition}
      longestY={longestY}
      onClick={onClick ? (params) => onClick({ config, params, point: params?.data?.row }) : undefined}
      onHover={onHover ? (params) => onHover({ config, params, point: params?.data?.row }) : undefined}
      onLegendSelect={onLegendSelect ? (params) => onLegendSelect({ config, params }) : undefined}
      onMouseOut={onMouseOut ? (params) => onMouseOut({ config, params }) : undefined}
      option={optionWithAxis}
      title={title}
      tooltip={description}
      {...{ secondYAxisRange, xAxisRange, yAxisRange }}
    />
  );
};

const DynamicScatterChartMultithread = ({ isConfigSetting, xDataKey, ...props }) => {
  const {
    chartsConfigs,
    config,
    data: withoutIndexData,
    dataTransformatorMultithread,
    description,
    getColor,
    getColumnDisplayName,
    getDataTransformatorMemoDependencyArray,
    getUnit,
    hoverEffect,
    id,
    isFullScreen,
    isItemHovered,
    legendsPosition,
    onClick,
    onHover,
    onLegendSelect,
    onMouseOut,
    secondYAxisName: secondYAxisNameConfig,
    secondYAxisRange,
    somethingIsHovered,
    title,
    xAxisName: xAxisNameConfig,
    xAxisRange,
    yAxisName: yAxisNameConfig,

    yAxisRange,
  } = props;
  const { diagrams, invertXAxis, invertYAxis } = config;
  const selectedConfigs = chartsConfigs?.filter((config) => config.dataKey);

  const dataTransformatorDependency = getDataTransformatorMemoDependencyArray({
    config,
  });

  const [flattenData, setFlattenData] = useState([]);

  const {
    dataAggregatorDecorator,
    dataTransformatorDecorator,
    isPainting,
    isProcessing,
    option,
    setIsPainting,
    setOption,
  } = useDynamicDashboardCalculations();

  const activeWorkerRef = useRef(null);
  const workerId = useRef(0);
  useEffect(() => {
    if (activeWorkerRef.current?.terminate) {
      activeWorkerRef.current.terminate();
    }

    workerId.current += 1;

    const workerInstance = dataTransformatorMultithread();
    activeWorkerRef.current = workerInstance;
    const getTransformedData = async () => {
      const { flattenData: newFlattenData, requestId } = await workerInstance.calculate(
        withoutIndexData,
        { ...config },
        workerId.current
      );
      if (requestId === workerId.current) {
        setFlattenData(newFlattenData);
      }
    };

    if (!withoutIndexData || isConfigSetting) return;
    dataTransformatorDecorator(getTransformedData);

    return () => {
      if (workerInstance.terminate) {
        workerInstance.terminate();
      }
    };
  }, [JSON.stringify(dataTransformatorDependency), withoutIndexData, isConfigSetting]);

  const xIsNumeric = flattenData?.some((row) => isNumeric(row[xDataKey]));

  const [sortedData, setSortedData] = useState([]);
  const [xAxisValues, SetXAxisValues] = useState([]);
  const [data, setData] = useState([]);

  const activeAggregaterWorkerRef = useRef(null);
  const aggregaterWorkerId = useRef(0);
  useEffect(
    () => {
      if (activeAggregaterWorkerRef.current) {
        activeAggregaterWorkerRef.current?.terminate();
      }

      aggregaterWorkerId.current += 1;

      const worker = new Worker(new URL("../../../src/scatterChart.worker.js", import.meta.url));
      activeAggregaterWorkerRef.current = worker;

      const runWorker = async (...props) => {
        const scatterChartDataAggregater = Comlink.wrap(worker);
        const result = await scatterChartDataAggregater(...props);
        return result;
      };

      const aggregater = async () => {
        let {
          data: dataTemp,
          requestId,
          sortedData: sortedDataTemp,
          xAxisValues: xAxisValuesTemp,
        } = await runWorker(
          {
            flattenData,
            xDataKey,
            xIsNumeric,
          },
          aggregaterWorkerId.current
        );

        if (requestId === aggregaterWorkerId.current) {
          setSortedData(sortedDataTemp);
          SetXAxisValues(xAxisValuesTemp);
          setData(dataTemp);
        }
      };

      if (!flattenData || isConfigSetting) return;
      dataAggregatorDecorator(aggregater, worker);

      return () => {
        worker.terminate();
      };
    },
    [flattenData, xDataKey, JSON.stringify(selectedConfigs), isConfigSetting]
    // [partitions, stacks, sort, flattenData]
  );

  useEffect(() => {
    if (isConfigSetting || !flattenData) return;
    const newOptions = getScatterChartOptions({
      config,
      diagrams,
      getColor,
      highPerformance: true,
      hoverEffect,
      isItemHovered,
      selectedConfigs,
      somethingIsHovered,
      sortedData,
      xDataKey,
    });
    setOption({ ...newOptions });
  }, [
    somethingIsHovered,
    JSON.stringify(selectedConfigs),
    diagrams,
    isItemHovered,
    config?.chartType,
    xDataKey,
    config.xAxis,
    config?.yAxis,
    sortedData,
    getColor,
    isConfigSetting,
  ]);

  const { xAxis, yAxis } = useGetScatterChartAxisOption({
    config,
    getColumnDisplayName,
    getUnit,
    invertXAxis,
    invertYAxis,
    secondYAxisNameConfig,
    selectedConfigs,
    xAxisNameConfig,
    xAxisValues,
    xDataKey,
    xIsNumeric,
    yAxisNameConfig,
  });

  const optionWithAxis = { ...option, xAxis, yAxis };

  useScatterChartDataDownload({ data, id, selectedConfigs, xDataKey });

  const longestY = getArrayLongestElement(optionWithAxis.series?.map((s) => s.maxValue ?? 0));

  if (isConfigSetting) return;
  return (
    <DynamicEchartsChartWithLoader
      getLegendDisplayName={(colName) => getColumnDisplayName({ colName })}
      isFullScreen={isFullScreen}
      isPainting={isPainting}
      isProcessing={isProcessing}
      legendsPosition={legendsPosition}
      longestY={longestY}
      onClick={onClick ? (params) => onClick({ config, params, point: params?.data?.row }) : undefined}
      onFinished={() => {
        setIsPainting(false);
      }}
      onHover={onHover ? (params) => onHover({ config, params, point: params?.data?.row }) : undefined}
      onLegendSelect={onLegendSelect ? (params) => onLegendSelect({ config, params }) : undefined}
      onMouseOut={onMouseOut ? (params) => onMouseOut({ config, params }) : undefined}
      option={optionWithAxis}
      setIsPainting={setIsPainting}
      title={title}
      tooltip={description}
      {...{ secondYAxisRange, xAxisRange, yAxisRange }}
    />
  );
};

const DynamicScatterChart = ({ xDataKey, ...props }) => {
  const {
    chartsConfigs,
    config,
    data: withoutIndexData,
    dataTransformator,
    description,
    getColor,
    getColumnDisplayName,
    getDataTransformatorMemoDependencyArray,
    getUnit,
    hoverEffect,
    id,
    isFullScreen,
    isItemHovered,
    legendsPosition,
    onClick,
    onHover,
    onLegendSelect,
    onMouseOut,
    secondYAxisName: secondYAxisNameConfig,
    secondYAxisRange,
    somethingIsHovered,
    title,
    xAxisName: xAxisNameConfig,
    xAxisRange,
    yAxisName: yAxisNameConfig,
    yAxisRange,
  } = props;

  const { diagrams, invertXAxis, invertYAxis } = config;
  const selectedConfigs = chartsConfigs?.filter((config) => config.dataKey);

  const dataTransformatorDependency = getDataTransformatorMemoDependencyArray({
    config,
  });
  let { flattenData } = useMemo(
    () => dataTransformator(withoutIndexData, { ...config }),
    [dataTransformatorDependency, withoutIndexData]
  );

  const { data, sortedData } = useMemo(() => {
    if (!flattenData?.length || !xDataKey) return {};

    const data = flattenData?.map((d, index) => ({
      ...d,
      index,
    }));

    const sortedData = sortData({ data, dataKey: xDataKey });

    return { data, sortedData };
  }, [flattenData, xDataKey, JSON.stringify(selectedConfigs)]);

  const xIsNumeric = flattenData?.some((row) => isNumeric(row[xDataKey]));
  const xAxisValues = useMemo(() => {
    return !xIsNumeric && [...new Set(sortedData?.map((row) => row[xDataKey] ?? []))];
  }, [xDataKey, sortedData, xIsNumeric]);

  const option = useMemo(() => {
    return getScatterChartOptions({
      config,
      diagrams,
      getColor,
      highPerformance: true,
      hoverEffect,
      isItemHovered,
      selectedConfigs,
      somethingIsHovered,
      sortedData,
      xDataKey,
    });
  }, [
    somethingIsHovered,
    isItemHovered,
    JSON.stringify(selectedConfigs),
    config?.xAxis,
    diagrams,
    sortedData,
    config,
    getColor,
    hoverEffect,
    config?.chartType,
    xDataKey,
    config?.yAxis,
  ]);

  const { xAxis, yAxis } = useGetScatterChartAxisOption({
    config,
    getColumnDisplayName,
    getUnit,
    invertXAxis,
    invertYAxis,
    secondYAxisNameConfig,
    selectedConfigs,
    xAxisNameConfig,
    xAxisValues,
    xDataKey,
    xIsNumeric,
    yAxisNameConfig,
  });
  const optionWithAxis = { ...option, xAxis, yAxis };

  useScatterChartDataDownload({ data, id, selectedConfigs, xDataKey });

  const longestY = getArrayLongestElement(optionWithAxis.series?.map((s) => s.maxValue ?? 0));
  return (
    <DynamicEchartsChart
      getLegendDisplayName={(colName) => getColumnDisplayName({ colName })}
      isFullScreen={isFullScreen}
      legendsPosition={legendsPosition}
      longestY={longestY}
      onClick={onClick ? (params) => onClick({ config, params, point: params?.data?.row }) : undefined}
      onHover={onHover ? (params) => onHover({ config, params, point: params?.data?.row }) : undefined}
      onLegendSelect={onLegendSelect ? (params) => onLegendSelect({ config, params }) : undefined}
      onMouseOut={onMouseOut ? (params) => onMouseOut({ config, params }) : undefined}
      option={optionWithAxis}
      title={title}
      tooltip={description}
      {...{ secondYAxisRange, xAxisRange, yAxisRange }}
    />
  );
};

const generateHistData = ({ axisValues, data, dataKeys, filters }) => {
  if (!data?.length || !axisValues?.length) return;
  const axisValuesDisplay = axisValues?.map(echartsDefaultTooltipFormatter);
  const histData = [];
  for (const i in axisValues) {
    if (i == 0) continue;
    const min = axisValues[i - 1];
    const max = axisValues[i];
    const allCounts = {};
    for (const dataKeyIndex in dataKeys) {
      const dataKey = dataKeys[dataKeyIndex];
      const dataKeyFilters = filters[dataKeyIndex];

      const count = data
        ?.filter((row) => {
          for (const [filterName, filterValue] of dataKeyFilters) {
            if (row[filterName] !== filterValue && filterValue !== undefined) return false;
          }
          return true;
        })
        ?.reduce((total, current) => {
          const value = current[dataKey];
          if ((value > min || (value === min && i === 1)) && value <= max) total++;
          return total;
        }, 0);
      allCounts[dataKey] = count;
    }

    const label = `${axisValuesDisplay[i - 1]} - ${axisValuesDisplay[i]}`;
    histData.push({ name: label, ...allCounts });
  }
  return histData;
};

const DynamicHistogramChart = (props) => {
  const {
    chartsConfigs,
    config,
    data,
    dataTransformator,
    description,
    getColor,
    getColumnDisplayName,
    getDataTransformatorMemoDependencyArray,
    hoverEffect,
    id,
    isFullScreen,
    isItemHovered,
    legendsPosition,
    onClick,
    onHover,
    onLegendSelect,
    onMouseOut,
    somethingIsHovered,
    sort,
    title,
    xAxisName: xAxisNameConfig,
  } = props;
  const { invertXAxis, invertYAxis } = config;

  const diagramsFiltersNames = Object.keys(chartsConfigs?.[0])?.filter((key) =>
    key.startsWith(valuesEnums.DIAGRAM_FILTER_PREFIX)
  );

  const dispatch = useDispatch();
  const dataKeys = chartsConfigs?.filter((chartConfig) => chartConfig)?.map((chartConfig) => chartConfig.dataKey);
  const dataFilters = chartsConfigs
    ?.filter((chartConfig) => chartConfig)
    ?.map((chartConfig) => {
      const activeFiltersNames = diagramsFiltersNames?.filter((col) => chartConfig[col] !== undefined);
      const activeFiltersDataKeysAndValues = activeFiltersNames?.map((col) => [
        getDataKeyFromDiagramFilterName({ filterName: col }),
        chartConfig[col],
      ]);
      return activeFiltersDataKeysAndValues;
    });

  const dataTransformatorDependency = getDataTransformatorMemoDependencyArray({
    config,
  });
  let { flattenData } = useMemo(() => dataTransformator(data, { ...config }), [dataTransformatorDependency, data]);

  const { histData } = useMemo(() => {
    const minMax = findDataRange({ data: flattenData, dataKeys });
    const { max, min } = minMax ?? {};
    const axisValues = generateAxisValues(min, max);

    let histData = generateHistData({
      axisValues,
      data: flattenData ?? [],
      dataKeys,
      filters: dataFilters,
    });
    if (sort === "y") {
      histData = sortDataAccumulative({ data: histData, dataKeys });
    }

    return { histData };
  }, [flattenData, JSON.stringify(chartsConfigs), sort]);

  var option = useMemo(() => {
    return {
      series: chartsConfigs?.map((chartConfig, i) => {
        const activeFiltersNames = diagramsFiltersNames?.filter((col) => chartConfig[col] !== undefined);
        const activeFiltersNamesAndValues = activeFiltersNames?.map((col) => [col, chartConfig[col]]);
        const seriesMixedName = getSeriesMixedName({
          filters: activeFiltersNamesAndValues,
          seriesName: chartConfig.dataKey,
        });
        const color =
          getColor &&
          getColor({
            chartType: config?.chartType,
            dataPoint: chartConfig.dataKey,
            diagrams: chartsConfigs,
            quantity: seriesMixedName,
          });
        return {
          color: color ?? getItemColor(i),
          data: histData?.map((row, index) => {
            return {
              itemStyle: {
                opacity:
                  somethingIsHovered &&
                  isItemHovered &&
                  !isItemHovered({
                    chartType: config?.chartType,
                    diagrams: chartsConfigs,
                    row: {
                      dataPoint: chartConfig?.dataKey,
                      index,
                      rangeString: row.name,
                    },
                    xAxis: config?.xAxis,
                    yAxis: config?.yAxis,
                  })
                    ? 0.05
                    : 1,
              },
              row: {
                dataPoint: chartConfig?.dataKey,
                index,
                rangeString: row.name,
              },
              value: row[chartConfig?.dataKey],
            };
          }),
          emphasis: {
            focus: hoverEffect ? undefined : "series",
          },
          maxValue: max(histData?.map((row) => row[chartConfig?.dataKey])),
          name: seriesMixedName,
          stack: i,
          tooltip: {
            valueFormatter: echartsDefaultTooltipFormatter,
          },
          type: "bar",
        };
      }),
    };
  }, [
    somethingIsHovered,
    isItemHovered,
    histData,
    JSON.stringify(chartsConfigs),
    config?.yAxis,
    config?.xAxis,
    getColor,
  ]);

  const optionWithAxis = {
    ...option,
    xAxis: [
      {
        data: histData?.map((row) => row.name),
        inverse: invertXAxis,
        name: xAxisNameConfig,
        nameGap: 30,
        nameLocation: "center",
      },
    ],
    yAxis: [
      {
        inverse: invertYAxis,
        name: valuesEnums.COUNT,
        type: "value",
      },
    ],
  };

  useEffect(() => {
    dispatch(
      setDataToDownload({
        data: histData?.map((row) => ({ ...row, name: `[${row?.name}]` })),
        id,
      })
    );
  }, [histData, id]);

  return (
    <DynamicEchartsChart
      getLegendDisplayName={(colName) => getColumnDisplayName({ colName })}
      isFullScreen={isFullScreen}
      legendsPosition={legendsPosition}
      longestY={Math.max(...(optionWithAxis.series?.map((s) => s.maxValue ?? 0) || []))}
      onClick={onClick ? (params) => onClick({ config, params, point: params?.data?.row }) : undefined}
      onHover={onHover ? (params) => onHover({ config, params, point: params?.data?.row }) : undefined}
      onLegendSelect={onLegendSelect ? (params) => onLegendSelect({ config, params }) : undefined}
      onMouseOut={onMouseOut ? (params) => onMouseOut({ config, params }) : undefined}
      option={optionWithAxis}
      title={title}
      tooltip={description}
    />
  );
};

// const echartsDefaultTooltipNameFormatter = (params, getColumnDisplayName) => {
//   const name = getColumnDisplayName({ colName: !params.dimensionNames?.length ? params.name : params.seriesName })
//   const value = formatNumberBasedOnUser(params.value)
//   return params.marker + ' ' + name + ': ' + value
//   return `
//   ${params.marker} ${params.seriesName}: <br/><strong>${params.name}</strong>
//   Value: <strong>${value}</strong>
// `;
// }

const legendsWidth = 200;
const legendsWidthFullScreen = 400;

const echartsDefaultOptions = {
  animation: false,
  dataZoom: {
    bottom: 30,
    height: 16,
    type: "slider",
  },
  toolbox: {
    feature: {
      saveAsImage: { show: false },
    },
  },
  tooltip: {
    axisPointer: {
      crossStyle: {
        color: "#999",
      },
      type: "cross",
    },
    position: "top",
    trigger: "item",
  },
};

const legendConfigAtRight = {
  grid: {
    bottom: 75,
    containLabel: true,
    left: 70,
    right: legendsWidth + 90,
    top: 55,
  },
  legend: {
    align: "right",
    left: "right",
    orient: "vertical",
    textStyle: {
      fontFamily: getCSSVar("--font-primary"),
      fontSize: "1.5rem",
      fontWeight: 600,
      overflow: "truncate",
      width: legendsWidth,
    },
    top: 40,
    type: "scroll",
  },
};

export const legendsConfigAtBottom = {
  grid: {
    bottom: 75,
    containLabel: true,
    left: 70,
    right: 80,
    top: 55,
  },
  legend: {
    bottom: 0,
    left: 10,
    orient: "horizontal",
    right: 40,
    textStyle: {
      fontFamily: getCSSVar("--font-primary"),
      fontSize: "1.3rem",
      fontWeight: 600,
      width: legendsWidth,
    },
    type: "scroll",
  },
};

const getAxisRangeOption = (axisRange) => {
  return axisRange && (isNumeric(axisRange?.[0]) || axisRange?.[0]) && (isNumeric(axisRange?.[1]) || axisRange?.[1])
    ? {
        max: Number(axisRange?.[1]),
        min: Number(axisRange?.[0]),
      }
    : {};
};

export const DynamicEchartsChart = ({ option, secondYAxisRange, title, tooltip, xAxisRange, yAxisRange, ...props }) => {
  const eChartsRef = useRef();
  const { lazyUpdate, notMerge = true, replaceMerge, style, ...echartsProps } = props;

  let mergedOptions = {
    ...echartsDefaultOptions,
  };

  if (props?.legendsPosition === "right") {
    mergedOptions = { ...mergedOptions, ...legendConfigAtRight };
    if (props.isFullScreen) {
      mergedOptions = {
        ...mergedOptions,
        grid: {
          ...mergedOptions.grid,
          right: legendsWidthFullScreen + 30,
        },
        legend: {
          ...mergedOptions.legend,
          textStyle: {
            ...mergedOptions.legend.textStyle,
            width: legendsWidthFullScreen,
          },
        },
      };
    }
  } else {
    mergedOptions = { ...mergedOptions, ...legendsConfigAtBottom };
  }

  mergedOptions = {
    ...mergedOptions,
    legend: {
      ...mergedOptions.legend,
      formatter: (name) => {
        if (props.getLegendDisplayName) return props.getLegendDisplayName?.(name) ?? name;
        return name;
      },
    },
  };

  if (option)
    mergedOptions = {
      ...mergedOptions,
      ...option,
      grid: { ...mergedOptions?.grid, ...option?.grid },
    };

  if (mergedOptions?.yAxis) {
    let maxLabelLength;
    if (typeof props?.longestY === "number" && isNumeric(parseInt(props?.longestY)))
      maxLabelLength = parseInt(props?.longestY)?.toString()?.length;
    else if (typeof props?.longestY === "string") {
      maxLabelLength = props?.longestY?.length;
    } else {
      maxLabelLength = 5;
    }

    // Just a silly formulae for getting a gap for length of a number or text. Make it better if you can
    const nameGap = maxLabelLength ** 0.4 * 30 + maxLabelLength ** 0.7 * 4 + 10;

    const isYInverted = mergedOptions?.yAxis?.[0]?.inverse;
    const bottomGrid = mergedOptions?.grid?.bottom;
    const topGrid = mergedOptions?.grid?.top;
    mergedOptions = {
      ...mergedOptions,
      grid: {
        ...mergedOptions?.grid,
        bottom: isYInverted ? bottomGrid + 20 : bottomGrid,
        top: isYInverted ? topGrid - 20 : topGrid,
      },
      yAxis: mergedOptions?.yAxis?.map((axis, index) => {
        const minMaxOption = index === 0 ? getAxisRangeOption(yAxisRange) : getAxisRangeOption(secondYAxisRange);
        return {
          axisLabel: {
            formatter: (value) => props.getLegendDisplayName?.(value) ?? value,
          },
          boundaryGap: props.isVertical,
          nameGap,
          nameLocation: "middle",
          nameRotate: 90,
          nameTextStyle: {
            verticalAlign: "left",
          },
          scale: true,
          ...minMaxOption,
          ...axis,
        };
      }),
    };
  }

  if (mergedOptions?.xAxis) {
    mergedOptions = {
      ...mergedOptions,
      xAxis: [
        {
          axisLabel: {
            formatter: (value) => props.getLegendDisplayName?.(value) ?? value,
          },
          boundaryGap: true,
          scale: true,
          ...getAxisRangeOption(xAxisRange),
          ...mergedOptions?.xAxis?.[0],
        },
      ],
    };
  }

  let clickTimeout = null;
  useEffect(() => {
    if (eChartsRef && eChartsRef.current) {
      const chartInstance = eChartsRef.current.getEchartsInstance();

      chartInstance.setOption(mergedOptions, { lazyUpdate, notMerge, replaceMerge });

      const handleLegendSelect = (params) => {
        if (clickTimeout) {
          clearTimeout(clickTimeout);
          clickTimeout = null;

          props.onLegendSelect?.(params);

          chartInstance.dispatchAction({
            name: params.name,
            type: "legendSelect",
          });
        } else {
          clickTimeout = setTimeout(() => {
            clickTimeout = null;
          }, 250);
        }
      };

      chartInstance.on("legendselectchanged", handleLegendSelect);
      chartInstance.on("mouseover", props.onHover);
      chartInstance.on("mouseout", props.onMouseOut);
      chartInstance.on("click", props.onClick);

      if (props.onFinished) {
        if (chartInstance) {
          setTimeout(() => {
            props.onFinished();
          }, 500);
        }
      }

      return () => {
        chartInstance.off("legendselectchanged", handleLegendSelect);
        chartInstance.off("mouseover", props.onHover);
        chartInstance.off("mouseout", props.onMouseOut);
        chartInstance.off("click", props.onClick);
      };
    }
  }, [
    lazyUpdate,
    notMerge,
    option,
    props.onClick,
    props.onFinished,
    props.onHover,
    props.onLegendSelect,
    props.onMouseOut,
    replaceMerge,
  ]);

  const { className } = useDelayedTransitionedDisplay();

  return (
    <Stack className={cn("h-full w-full flex-col text-center", className)}>
      <DynamicChartTitle title={title} tooltip={tooltip} />
      <ReactECharts
        className="flex-1"
        lazyUpdate={lazyUpdate}
        notMerge={notMerge}
        option={mergedOptions}
        ref={eChartsRef}
        replaceMerge={replaceMerge}
        style={{ width: "100%", ...style }}
        {...echartsProps}
      />
    </Stack>
  );
};

const DynamicPieChart = ({ chartsConfigs, data, ...props }) => {
  let {
    chartType,
    config,
    dataPoint,
    dataTransformator,
    description,
    getColor,
    getColumnDisplayName,
    getColumnSortFunction,
    getDataTransformatorMemoDependencyArray,
    height,
    hoverEffect,
    id,
    isFullScreen,
    isItemHovered,
    legendsPosition,
    onClick,
    onHover,
    onLegendSelect,
    onMouseOut,
    somethingIsHovered,
    title,
    width,
  } = props;
  const dispatch = useDispatch();

  const aggregationFunction = props.aggregationFunction ?? aggregatedSumValue;

  let { invertXAxis, showPieLabels, subDataPoint, xAxis, yAxis } = config;
  if (subDataPoint) dataPoint = subDataPoint;
  const diagramConfig = chartsConfigs?.[0] ?? {};
  const { partitions, stacks } = diagramConfig;

  const dataTransformatorDependency = getDataTransformatorMemoDependencyArray({
    config,
  });
  let { dataStacks: preCalculatedStacks, flattenData } = useMemo(
    () => dataTransformator(data, { ...config }),
    [dataTransformatorDependency, data]
  );

  const { allAggregatedData, dataStacks } = useMemo(() => {
    // let flattenData = dataTransformator(data, { dataPoint, chartType, diagrams: chartsConfigs })

    let dataStacks;
    if (!stacks) dataStacks = [];
    else {
      dataStacks = preCalculatedStacks ?? flattenData?.map((e) => e[stacks])?.filter(distinctFilter);
    }

    const stacksSortFunction = (a, b) => {
      if (getColumnSortFunction) return getColumnSortFunction({ aValue: a, bValue: b, dataKey: stacks });
      return a > b ? 1 : -1;
    };
    dataStacks.sort(stacksSortFunction);

    let allAggregatedData = [];

    if (!dataStacks.length) {
      allAggregatedData.push(
        aggregationFunction({
          aggregateColumns: [dataPoint],
          data: flattenData,
          groupByColumns: [partitions],
        })
      );
    } else {
      for (const stack of dataStacks) {
        const filteredData = flattenData.filter((e) => e[stacks] === stack);

        allAggregatedData.push(
          aggregationFunction({
            aggregateColumns: [dataPoint],
            data: filteredData,
            groupByColumns: [partitions],
          })
        );
      }
    }

    return { allAggregatedData, dataStacks };
  }, [
    data,
    dataTransformatorDependency,
    props.aggregationFunction,
    dataPoint,
    chartType,
    partitions,
    stacks,
    xAxis,
    invertXAxis,
    subDataPoint,
  ]);

  const partitionsSortFunction = (a, b) => {
    if (getColumnSortFunction)
      return getColumnSortFunction({
        aValue: a[partitions],
        bValue: b[partitions],
        dataKey: partitions,
      });
    return a[partitions] < b[partitions] ? -1 : 1;
  };

  var option = useMemo(() => {
    const optionsProps = {
      allAggregatedData,
      chartsConfigs,
      chartType,
      dataPoint,
      dataStacks,
      getColor,
      getColumnDisplayName,
      getItemColor,
      height,
      hoverEffect,
      isItemHovered,
      legendsPosition,
      legendsWidth,
      partitions,
      partitionsSortFunction,
      showPieLabels,
      somethingIsHovered,
      stacks,
      width,
      xAxis,
      yAxis,
    };
    return getPieAndDoughnutOptions(optionsProps);
  }, [
    somethingIsHovered,
    allAggregatedData,
    dataPoint,
    subDataPoint,
    chartType,
    partitions,
    stacks,
    xAxis,
    getColor,
    isItemHovered,
    showPieLabels,
  ]);

  useEffect(() => {
    const dataToDownload = allAggregatedData?.reduce((total, current, stackIndex) => {
      const name = dataStacks[stackIndex];
      return [...total, ...current.map((r) => ({ ...r, name }))];
    }, []);

    dispatch(setDataToDownload({ data: dataToDownload, id: id }));
  }, [allAggregatedData, dataStacks, id]);

  return (
    <DynamicEchartsChart
      getLegendDisplayName={(colName) => getColumnDisplayName({ colName })}
      isFullScreen={isFullScreen}
      legendsPosition={legendsPosition}
      onClick={onClick ? (params) => onClick({ config, params, point: params?.data?.row }) : undefined}
      onHover={onHover ? (params) => onHover({ config, params, point: params?.data?.row }) : undefined}
      onLegendSelect={onLegendSelect ? (params) => onLegendSelect({ config, params }) : undefined}
      onMouseOut={onMouseOut ? (params) => onMouseOut({ config, params }) : undefined}
      option={option}
      title={title}
      tooltip={description}
    />
  );
};

const DynamicPieChartMultithread = ({ chartsConfigs, data, ...props }) => {
  let {
    chartType,
    config,
    dataPoint,
    dataTransformatorMultithread,
    description,
    getColor,
    getColumnDisplayName,
    getColumnSortFunction,
    getDataTransformatorMemoDependencyArray,
    height,
    hoverEffect,
    id,
    isConfigSetting,
    isFullScreen,
    isItemHovered,
    legendsPosition,
    onClick,
    onHover,
    onLegendSelect,
    onMouseOut,
    somethingIsHovered,
    title,
    width,
  } = props;
  const dispatch = useDispatch();
  const aggregationFunction = props.aggregationFunction ?? aggregatedSumValue;

  let { invertXAxis, showPieLabels, subDataPoint, xAxis, yAxis } = config;
  if (subDataPoint) dataPoint = subDataPoint;
  const diagramConfig = chartsConfigs?.[0] ?? {};
  const { partitions, stacks } = diagramConfig;

  const dataTransformatorDependency = getDataTransformatorMemoDependencyArray({
    config,
  });

  const [flattenData, setFlattenData] = useState([]);
  const {
    dataAggregatorDecorator,
    dataTransformatorDecorator,
    isPainting,
    isProcessing,
    option,
    setIsPainting,
    setOption,
  } = useDynamicDashboardCalculations();

  const activeWorkerRef = useRef(null);
  const workerId = useRef(0);
  useEffect(() => {
    if (activeWorkerRef.current?.terminate) {
      activeWorkerRef.current.terminate();
    }

    workerId.current += 1;

    const workerInstance = dataTransformatorMultithread();
    activeWorkerRef.current = workerInstance;

    const getTransformedData = async () => {
      const { flattenData: newFlattenData, requestId } = await workerInstance.calculate(
        data,
        { ...config },
        workerId.current
      );
      if (requestId === workerId.current) {
        setFlattenData(newFlattenData);
      }
    };

    if (!data || isConfigSetting) return;
    dataTransformatorDecorator(getTransformedData);

    return () => {
      if (workerInstance.terminate) {
        workerInstance.terminate();
      }
    };
  }, [JSON.stringify(dataTransformatorDependency), data, isConfigSetting]);

  const [allAggregatedData, SetAllAggregatedData] = useState([]);
  const [dataStacks, SetDataStacks] = useState([]);

  const stacksSortFunction = (a, b) => {
    if (getColumnSortFunction) return getColumnSortFunction({ aValue: a, bValue: b, dataKey: stacks });
    return a > b ? 1 : -1;
  };

  const aggregaterWorkerRef = useRef(null);
  const aggregaterWorkerId = useRef(0);
  useEffect(() => {
    if (aggregaterWorkerRef.current) {
      aggregaterWorkerRef.current.terminate();
    }

    aggregaterWorkerId.current += 1;
    const worker = new Worker(new URL("../../../src/pieChart.worker.js", import.meta.url));
    aggregaterWorkerRef.current = worker;

    const runWorker = async (...props) => {
      const pieChartDataAggregater = Comlink.wrap(worker);
      const result = await pieChartDataAggregater(...props);
      return result;
    };
    const aggregater = async () => {
      let {
        allAggregatedData: allAggregatedDataTemp,
        dataStacks: dataStacksTemp,
        requestId,
      } = await runWorker(
        { dataPoint, flattenData, partitions, stacks },
        Comlink.proxy(stacksSortFunction),
        aggregaterWorkerId.current,
        Comlink.proxy(aggregationFunction)
      );
      if (requestId === aggregaterWorkerId.current) {
        SetAllAggregatedData(allAggregatedDataTemp);
        SetDataStacks(dataStacksTemp);
      }
    };

    if (!flattenData || isConfigSetting) return;
    dataAggregatorDecorator(aggregater, worker);
    return () => {
      worker.terminate();
    };
  }, [
    data,
    dataTransformatorDependency,
    dataPoint,
    chartType,
    partitions,
    stacks,
    xAxis,
    invertXAxis,
    subDataPoint,
    flattenData,
    isConfigSetting,
    props.aggregationFunction,
  ]);

  const partitionsSortFunction = (a, b) => {
    if (getColumnSortFunction)
      return getColumnSortFunction({
        aValue: a[partitions],
        bValue: b[partitions],
        dataKey: partitions,
      });
    return a[partitions] < b[partitions] ? -1 : 1;
  };

  useEffect(() => {
    if (isConfigSetting || !flattenData) return;

    const optionsProps = {
      allAggregatedData,
      chartsConfigs,
      chartType,
      dataPoint,
      dataStacks,
      getColor,
      getColumnDisplayName,
      getItemColor,
      height,
      hoverEffect,
      isItemHovered,
      legendsPosition,
      legendsWidth,
      partitions,
      partitionsSortFunction,
      showPieLabels,
      somethingIsHovered,
      stacks,
      width,
      xAxis,
      yAxis,
    };
    var optionMemoized = getPieAndDoughnutOptions(optionsProps);

    setOption(optionMemoized);
  }, [
    somethingIsHovered,
    allAggregatedData,
    dataPoint,
    subDataPoint,
    chartType,
    partitions,
    stacks,
    xAxis,
    getColor,
    isItemHovered,
    isConfigSetting,
    showPieLabels,
  ]);

  useEffect(() => {
    const dataToDownload = allAggregatedData?.reduce((total, current, stackIndex) => {
      const name = dataStacks[stackIndex];
      return [...total, ...current.map((r) => ({ ...r, name }))];
    }, []);
    dispatch(setDataToDownload({ data: dataToDownload, id: id }));
  }, [allAggregatedData, dataStacks, id]);

  if (isConfigSetting) return;
  return (
    <DynamicEchartsChartWithLoader
      getLegendDisplayName={(colName) => getColumnDisplayName({ colName })}
      isFullScreen={isFullScreen}
      isPainting={isPainting}
      isProcessing={isProcessing}
      legendsPosition={legendsPosition}
      onClick={onClick ? (params) => onClick({ config, params, point: params?.data?.row }) : undefined}
      onFinished={() => {
        setIsPainting(false);
      }}
      onHover={onHover ? (params) => onHover({ config, params, point: params?.data?.row }) : undefined}
      onLegendSelect={onLegendSelect ? (params) => onLegendSelect({ config, params }) : undefined}
      onMouseOut={onMouseOut ? (params) => onMouseOut({ config, params }) : undefined}
      option={option}
      setIsPainting={setIsPainting}
      title={title}
      tooltip={description}
    />
  );
};

const LegendItemWithValue = ({
  bulletHeight,
  bulletType = "rectangle",
  bulletWidth,
  className,
  color,
  direction,
  hasBullet,
  label,
  value,
  ...props
}) => {
  return (
    <Stack className={`legend w-full justify-between ${className} `} flexDirection={direction} spacing={4} {...props}>
      <LegendItem
        bulletHeight={bulletHeight ?? (hasBullet ? "0.4rem" : 0)}
        bulletType={bulletType}
        bulletWidth={bulletWidth ?? (hasBullet ? "0.8rem" : 0)}
        color={color}
        label={label}
      />
      <span className="text-lds-foreground-muted font-ds-body-bold-md">
        {/* {value >= 1000 ? addCommaToNumber(parseInt(value)) : value?.toFixed(2)}{" "} */}
        {formatNumberBasedOnUser(value)}
      </span>
    </Stack>
  );
};

const DynamicKPIMultithread = ({
  chartsConfigs,
  config,
  data: withoutIndexData,
  dataTransformatorMultithread,
  description,
  getColor,
  getColumnDisplayName,
  getColumnSortFunction,
  getDataTransformatorMemoDependencyArray,
  isConfigSetting,
  isItemHovered,
  onHover,
  onLegendSelect,
  onMouseOut,
  somethingIsHovered,
  ...props
}) => {
  const aggregationFunction = props.aggregationFunction ?? aggregatedSumValue;

  let { chartType, dataPoint, height, id, title, xDataKey } = props;
  const { subDataPoint } = config;
  if (subDataPoint) dataPoint = subDataPoint;
  const dispatch = useDispatch();
  const diagramConfig = chartsConfigs[0] ?? {};
  let { direction, partitions, stacks } = diagramConfig;

  if (chartType === chartTypesEnums.LINE || chartType === chartTypesEnums.AREA) stacks = null;
  if (chartType === chartTypesEnums.KPI) xDataKey = null;

  const isStacked = partitions || stacks;

  const data = withoutIndexData?.map((d, index) => ({
    ...d,
    index,
  }));
  const dataTransformatorDependency = getDataTransformatorMemoDependencyArray({
    config,
  });

  const [flattenData, setFlattenData] = useState([]);
  const [preCalculatedStacks, setPreCalculatedStacks] = useState([]);
  const [preCalculatedPartitions, setPreCalculatedPartitions] = useState([]);

  const { dataAggregatorDecorator, dataTransformatorDecorator, isProcessing, option, setOption } =
    useDynamicDashboardCalculations();

  const activeWorkerRef = useRef(null);
  const workerId = useRef(0);
  useEffect(() => {
    if (activeWorkerRef.current?.terminate) {
      activeWorkerRef.current.terminate();
    }

    workerId.current += 1;

    const workerInstance = dataTransformatorMultithread();
    activeWorkerRef.current = workerInstance;

    const getTransformedData = async () => {
      const {
        dataPartitions: preCalculatedPartitions,
        dataStacks: preCalculatedStacks,
        flattenData: newFlattenData,
        requestId,
      } = await workerInstance.calculate(data, { ...config }, workerId.current);
      if (requestId === workerId.current) {
        setFlattenData(newFlattenData);
        setPreCalculatedStacks(preCalculatedStacks);
        setPreCalculatedPartitions(preCalculatedPartitions);
      }
    };

    if (!data || isConfigSetting) return;
    dataTransformatorDecorator(getTransformedData);

    return () => {
      if (workerInstance.terminate) {
        workerInstance.terminate();
      }
    };
  }, [JSON.stringify(dataTransformatorDependency), withoutIndexData]);

  const [allAggregatedData, SetAllAggregatedData] = useState([]);
  const [dataStacks, SetDataStacks] = useState([]);
  const [dataPartitions, SetDataPartitions] = useState([]);

  const sortFunction = (a, b, dataKey) => {
    if (getColumnSortFunction) return getColumnSortFunction({ aValue: a, bValue: b, dataKey });
    return defaultSortFunction(a, b);
  };

  const activeAggregaterWorkerRef = useRef(null);
  const aggregaterWorkerId = useRef(0);
  useEffect(() => {
    if (activeAggregaterWorkerRef.current) {
      activeAggregaterWorkerRef.current.terminate();
    }

    aggregaterWorkerId.current += 1;
    const worker = new Worker(new URL("../../../src/kpiChart.worker.js", import.meta.url));
    activeAggregaterWorkerRef.current = worker;
    const runWorker = async (...props) => {
      const kpiChartDataAggregater = Comlink.wrap(worker);
      const result = await kpiChartDataAggregater(...props);
      return result;
    };

    const aggregater = async () => {
      let {
        allAggregatedData: allAggregatedDataTemp,
        dataPartitions: dataPartitionsTemp,
        dataStacks: dataStacksTemp,
        requestId,
      } = await runWorker(
        {
          dataPoint,
          flattenData,
          isStacked,
          partitions,
          preCalculatedPartitions,
          preCalculatedStacks,
          stacks,
          xDataKey,
        },
        aggregaterWorkerId.current,
        Comlink.proxy(aggregationFunction)
      );
      if (requestId === aggregaterWorkerId.current) {
        SetAllAggregatedData(allAggregatedDataTemp);
        SetDataStacks(dataStacksTemp);
        SetDataPartitions(dataPartitionsTemp);
      }
    };

    if (!flattenData || isConfigSetting) return;
    dataAggregatorDecorator(aggregater, worker);

    return () => {
      worker.terminate();
    };
  }, [
    withoutIndexData,
    dataTransformatorDependency,
    dataPoint,
    subDataPoint,
    stacks,
    partitions,
    xDataKey,
    flattenData,
    isConfigSetting,
    props.aggregationFunction,
  ]);
  const isVertical = direction === "vertical";

  useEffect(() => {
    dispatch(setDataToDownload({ data: allAggregatedData, id: id }));
  }, [allAggregatedData, id, dispatch]);

  dataStacks.sort((a, b) => sortFunction(a, b, stacks));
  dataPartitions.sort((a, b) => sortFunction(a, b, partitions));

  const dataPointColor = getColor
    ? getColor({ chartType, dataPoint, diagrams: chartsConfigs, quantity: dataPoint })
    : undefined;

  useEffect(() => {
    if (isConfigSetting || !flattenData) return;
    let configsCalculations = {};

    if (partitions && !stacks) {
      dataPartitions?.forEach((p, j) => {
        const color = getColor && getColor({ chartType, dataPoint, diagrams: chartsConfigs, quantity: p });
        const isTransparent =
          somethingIsHovered &&
          isItemHovered &&
          !isItemHovered({
            chartType,
            diagrams: chartsConfigs,
            row: { [partitions]: p },
          });

        configsCalculations[p] = {
          color: color ?? getItemColor(0, j),
          isTransparent,
          value: allAggregatedData?.[0]?.[p],
        };
      });
    }

    if (partitions && stacks) {
      dataStacks?.forEach((s, i) => {
        configsCalculations[s] = {};
        dataPartitions?.forEach((p, j) => {
          const valueForColor = `(${s})-(${p})`;
          const color =
            getColor &&
            getColor({
              chartType,
              dataPoint,
              diagrams: chartsConfigs,
              quantity: valueForColor,
            });
          const isTransparent =
            somethingIsHovered &&
            isItemHovered &&
            !isItemHovered({
              chartType,
              diagrams: chartsConfigs,
              row: { [partitions]: p, [stacks]: s },
            });
          configsCalculations[s][p] = {
            color: color ?? getItemColor(i, j),
            isTransparent,
            value: allAggregatedData?.[0]?.[`(${s})-(${p})`],
            valueForColor,
          };
        });
      });
    }

    setOption(configsCalculations);
  }, [
    withoutIndexData,
    isItemHovered,
    flattenData,
    allAggregatedData,
    somethingIsHovered,
    dataPoint,
    partitions,
    stacks,
    subDataPoint,
    getColor,
    isConfigSetting,
  ]);

  const { className } = useDelayedTransitionedDisplay();

  return (
    <div className={cn("relative h-full w-full", className)}>
      <DynamicChartTitle title={title} tooltip={description} />
      {isProcessing && <ChartLoader className="absolute left-1 top-1  w-full" isCalculating={true} />}
      {!isProcessing && (
        <Stack
          className="w-full overflow-x-auto overflow-y-auto px-4"
          flexDirection={isVertical ? "column" : "row"}
          height={height - 48}
        >
          {!isStacked ? (
            <LegendItemWithValue
              bulletHeight="1.2rem"
              bulletType="circle"
              bulletWidth="1.2rem"
              className={`w-full py-1 ${onLegendSelect ? "cursor-pointer" : ""} }`}
              color={dataPointColor ?? getItemColor(0)}
              direction="column"
              hasBullet={true}
              label={getColumnDisplayName({ colName: dataPoint })}
              onClick={onLegendSelect ? () => onLegendSelect({ config, params: { name: dataPoint } }) : undefined}
              value={allAggregatedData?.[0]?.[dataPoint]}
            />
          ) : partitions && !stacks ? (
            dataPartitions?.map((p) => {
              const value = option?.[p]?.value;
              const color = option?.[p]?.color;
              const isTransparent = option?.[p]?.isTransparent;

              return (
                <LegendItemWithValue
                  bulletHeight="1.2rem"
                  bulletType="circle"
                  bulletWidth="1.2rem"
                  className={`w-full py-1  ${isTransparent ? "opacity-[0.2]" : "opacity-[1]"}
            ${onLegendSelect ? "cursor-pointer" : ""}  ${
                    somethingIsHovered && !isTransparent && "bg-lds-selection-subtle"
                  }`}
                  color={color}
                  direction={isVertical ? "row" : "column"}
                  hasBullet={true}
                  key={p}
                  label={getColumnDisplayName({ colName: p })}
                  onClick={onLegendSelect ? () => onLegendSelect({ config, params: { name: p } }) : undefined}
                  onMouseEnter={onHover ? () => onHover({ config, point: { [partitions]: p } }) : undefined}
                  onMouseLeave={onMouseOut ? () => onMouseOut({ config }) : undefined}
                  value={value}
                />
              );
            })
          ) : (
            <Stack
              className="h-full w-full"
              flexDirection={isVertical ? "column" : "row"}
              justifyContent="space-between"
            >
              {dataStacks?.map((s) => {
                return (
                  <div className="h-full w-full p-4 text-center capitalize font-ds-body-bold-md" key={s}>
                    <p className="mt-2">{getColumnDisplayName({ colName: s })}</p>
                    <Stack className="w-full" flexDirection="column" justifyContent="space-between">
                      {dataPartitions?.map((p) => {
                        const value = option?.[s]?.[p]?.value;
                        const valueForColor = option?.[s]?.[p]?.valueForColor;
                        const color = option?.[s]?.[p]?.color;
                        const isTransparent = option?.[s]?.[p]?.isTransparent;

                        return (
                          <LegendItemWithValue
                            bulletHeight="1.2rem"
                            bulletType="circle"
                            bulletWidth="1.2rem"
                            className={`w-full py-1 ${isTransparent ? "opacity-[0.2]" : "opacity-[1]"}
                           ${onLegendSelect ? "cursor-pointer" : ""}  ${
                              somethingIsHovered && !isTransparent && "bg-lds-selection-subtle"
                            }`}
                            color={color}
                            hasBullet={true}
                            key={p}
                            label={getColumnDisplayName({ colName: p })}
                            onClick={
                              onLegendSelect
                                ? () =>
                                    onLegendSelect({
                                      config,
                                      params: { name: valueForColor },
                                    })
                                : undefined
                            }
                            onMouseEnter={
                              onHover
                                ? () =>
                                    onHover({
                                      config,
                                      point: { [partitions]: p, [stacks]: s },
                                    })
                                : undefined
                            }
                            onMouseLeave={onMouseOut ? () => onMouseOut({ config }) : undefined}
                            value={value}
                          />
                        );
                      })}
                    </Stack>
                  </div>
                );
              })}
            </Stack>
          )}
          <Legend />
          <Tooltip />
        </Stack>
      )}
    </div>
  );
};

const DynamicKPI = ({
  chartsConfigs,
  config,
  data: withoutIndexData,
  dataTransformator,
  description,
  getColor,
  getColumnDisplayName,
  getColumnSortFunction,
  getDataTransformatorMemoDependencyArray,
  isItemHovered,
  onHover,
  onLegendSelect,
  onMouseOut,
  somethingIsHovered,
  ...props
}) => {
  const aggregationFunction = props.aggregationFunction ?? aggregatedSumValue;

  let { chartType, dataPoint, height, id, title, xDataKey } = props;
  const { subDataPoint } = config;
  if (subDataPoint) dataPoint = subDataPoint;
  const dispatch = useDispatch();
  const diagramConfig = chartsConfigs[0] ?? {};
  let { direction, partitions, stacks } = diagramConfig;

  if (chartType === chartTypesEnums.LINE || chartType === chartTypesEnums.AREA) stacks = null;
  if (chartType === chartTypesEnums.KPI) xDataKey = null;

  const isStacked = partitions || stacks;

  const data = withoutIndexData?.map((d, index) => ({
    ...d,
    index,
  }));
  const dataTransformatorDependency = getDataTransformatorMemoDependencyArray({
    config,
  });
  let {
    dataPartitions: preCalculatedPartitions,
    dataStacks: preCalculatedStacks,
    flattenData,
  } = useMemo(() => dataTransformator(data, { ...config }), [dataTransformatorDependency, withoutIndexData]);

  let { dataPartitions, dataStacks, kpiAggregatedDataMemoized } = useMemo(() => {
    let dataStacks;
    if (!stacks) dataStacks = [];
    else {
      dataStacks = preCalculatedStacks ?? flattenData?.map((e) => e[stacks])?.filter(distinctFilter);
    }

    let dataPartitions;
    if (!partitions) dataPartitions = [];
    else {
      dataPartitions = preCalculatedPartitions ?? flattenData?.map((e) => e[partitions])?.filter(distinctFilter);
    }

    const pivotColumn = partitions && stacks ? `(${stacks})-(${partitions})` : partitions ?? stacks;
    if (partitions && stacks) {
      flattenData = flattenData?.map((e) => ({
        ...e,
        [partitions]: null,
        [pivotColumn]: `(${e[stacks]})-(${e[partitions]})`,
        [stacks]: null,
      }));
    }

    let kpiAggregatedData;
    if (!isStacked) {
      kpiAggregatedData = aggregationFunction({
        aggregateColumns: [dataPoint],
        data: flattenData,
        groupByColumns: [],
      });
    } else {
      let pivotColumnValuesAll = dataStacks?.map((s) => dataPartitions?.map((p) => `(${s})-(${p})`));
      pivotColumnValuesAll = pivotColumnValuesAll?.reduce((total, current) => [...total, ...current], []);

      var pivotColumnValues =
        pivotColumn === partitions
          ? dataPartitions
          : pivotColumn === stacks
          ? dataStacks
          : pivotColumnValuesAll?.filter((sp) => flattenData?.some((row) => row[pivotColumn] === sp));

      // Remove hardcoded value
      flattenData = aggregationFunction({
        aggregateColumns: [dataPoint],
        data: flattenData,
        groupByColumns: [xDataKey, pivotColumn, "dataPoint"],
      });

      const pivotedData = pivotData(flattenData, pivotColumn, dataPoint);
      kpiAggregatedData = aggregationFunction({
        aggregateColumns: pivotColumnValues,
        data: pivotedData,
        groupByColumns: [],
      });
    }

    return {
      dataPartitions,
      dataStacks,
      kpiAggregatedDataMemoized: kpiAggregatedData,
    };
  }, [
    withoutIndexData,
    dataTransformatorDependency,
    dataPoint,
    props.aggregationFunction,
    subDataPoint,
    stacks,
    partitions,
    xDataKey,
  ]);

  const isVertical = direction === "vertical";

  useEffect(() => {
    dispatch(setDataToDownload({ data: kpiAggregatedDataMemoized, id: id }));
  }, [kpiAggregatedDataMemoized, id, dispatch]);

  const sortFunction = (a, b, dataKey) => {
    if (getColumnSortFunction) return getColumnSortFunction({ aValue: a, bValue: b, dataKey });
    return defaultSortFunction(a, b);
  };

  dataStacks.sort((a, b) => sortFunction(a, b, stacks));
  dataPartitions.sort((a, b) => sortFunction(a, b, partitions));

  const dataPointColor = getColor
    ? getColor({ chartType, dataPoint, diagrams: chartsConfigs, quantity: dataPoint })
    : undefined;

  const configsCalculationsMemoized = useMemo(() => {
    let configsCalculations = {};

    if (partitions && !stacks) {
      dataPartitions?.forEach((p, j) => {
        const color = getColor && getColor({ chartType, dataPoint, diagrams: chartsConfigs, quantity: p });
        const isTransparent =
          somethingIsHovered &&
          isItemHovered &&
          !isItemHovered({
            chartType,
            diagrams: chartsConfigs,
            row: { [partitions]: p },
          });

        configsCalculations[p] = {
          color: color ?? getItemColor(0, j),
          isTransparent,
          value: kpiAggregatedDataMemoized?.[0]?.[p],
        };
      });
    }

    if (partitions && stacks) {
      dataStacks?.forEach((s, i) => {
        configsCalculations[s] = {};
        dataPartitions?.forEach((p, j) => {
          const valueForColor = `(${s})-(${p})`;
          const color =
            getColor &&
            getColor({
              chartType,
              dataPoint,
              diagrams: chartsConfigs,
              quantity: valueForColor,
            });
          const isTransparent =
            somethingIsHovered &&
            isItemHovered &&
            !isItemHovered({
              chartType,
              diagrams: chartsConfigs,
              row: { [partitions]: p, [stacks]: s },
            });
          configsCalculations[s][p] = {
            color: color ?? getItemColor(i, j),
            isTransparent,
            value: kpiAggregatedDataMemoized?.[0]?.[`(${s})-(${p})`],
            valueForColor,
          };
        });
      });
    }
    return configsCalculations;
  }, [
    withoutIndexData,
    dataPoint,
    isItemHovered,
    dataPartitions,
    dataStacks,
    subDataPoint,
    stacks,
    partitions,
    getColor,
    kpiAggregatedDataMemoized,
    somethingIsHovered,
  ]);

  const { className } = useDelayedTransitionedDisplay();

  return (
    <>
      <DynamicChartTitle className={className} title={title} tooltip={description} />
      <Stack
        className={cn("w-full overflow-x-auto overflow-y-auto px-4", className)}
        flexDirection={isVertical ? "column" : "row"}
        height={height - 48}
      >
        {!isStacked ? (
          <LegendItemWithValue
            bulletHeight="1.2rem"
            bulletType="circle"
            bulletWidth="1.2rem"
            className={`w-full py-1 ${onLegendSelect ? "cursor-pointer" : ""} }`}
            color={dataPointColor ?? getItemColor(0)}
            direction="column"
            hasBullet={true}
            label={getColumnDisplayName({ colName: dataPoint })}
            onClick={onLegendSelect ? () => onLegendSelect({ config, params: { name: dataPoint } }) : undefined}
            value={kpiAggregatedDataMemoized?.[0]?.[dataPoint]}
          />
        ) : partitions && !stacks ? (
          dataPartitions?.map((p) => {
            const value = configsCalculationsMemoized?.[p]?.value;
            const color = configsCalculationsMemoized?.[p]?.color;
            const isTransparent = configsCalculationsMemoized?.[p]?.isTransparent;

            return (
              <LegendItemWithValue
                bulletHeight="1.2rem"
                bulletType="circle"
                bulletWidth="1.2rem"
                className={`w-full py-1  ${isTransparent ? "opacity-[0.2]" : "opacity-[1]"}
            ${onLegendSelect ? "cursor-pointer" : ""}  ${
                  somethingIsHovered && !isTransparent && "bg-lds-selection-subtle"
                }`}
                color={color}
                direction={isVertical ? "row" : "column"}
                hasBullet={true}
                key={p}
                label={getColumnDisplayName({ colName: p })}
                onClick={onLegendSelect ? () => onLegendSelect({ config, params: { name: p } }) : undefined}
                onMouseEnter={onHover ? () => onHover({ config, point: { [partitions]: p } }) : undefined}
                onMouseLeave={onMouseOut ? () => onMouseOut({ config }) : undefined}
                value={value}
              />
            );
          })
        ) : (
          <Stack className="h-full w-full" flexDirection={isVertical ? "column" : "row"} justifyContent="space-between">
            {dataStacks?.map((s) => {
              return (
                <div className="h-full w-full p-4 text-center capitalize font-ds-body-bold-md" key={s}>
                  <p className="mt-2">{getColumnDisplayName({ colName: s })}</p>
                  <Stack className="w-full" flexDirection="column" justifyContent="space-between">
                    {dataPartitions?.map((p) => {
                      const value = configsCalculationsMemoized?.[s]?.[p]?.value;
                      const valueForColor = configsCalculationsMemoized?.[s]?.[p]?.valueForColor;
                      const color = configsCalculationsMemoized?.[s]?.[p]?.color;
                      const isTransparent = configsCalculationsMemoized?.[s]?.[p]?.isTransparent;

                      return (
                        <LegendItemWithValue
                          bulletHeight="1.2rem"
                          bulletType="circle"
                          bulletWidth="1.2rem"
                          className={`w-full py-1 ${isTransparent ? "opacity-[0.2]" : "opacity-[1]"}
                           ${onLegendSelect ? "cursor-pointer" : ""}  ${
                            somethingIsHovered && !isTransparent && "bg-lds-selection-subtle"
                          }`}
                          color={color}
                          hasBullet={true}
                          key={p}
                          label={getColumnDisplayName({ colName: p })}
                          onClick={
                            onLegendSelect
                              ? () =>
                                  onLegendSelect({
                                    config,
                                    params: { name: valueForColor },
                                  })
                              : undefined
                          }
                          onMouseEnter={
                            onHover
                              ? () =>
                                  onHover({
                                    config,
                                    point: { [partitions]: p, [stacks]: s },
                                  })
                              : undefined
                          }
                          onMouseLeave={onMouseOut ? () => onMouseOut({ config }) : undefined}
                          value={value}
                        />
                      );
                    })}
                  </Stack>
                </div>
              );
            })}
          </Stack>
        )}
        <Legend />
        <Tooltip />
      </Stack>
    </>
  );
};

const DynamicStackedChart = ({
  chartsConfigs,
  config,
  data: withoutIndexData,
  dataTransformator,
  getDataTransformatorMemoDependencyArray,
  ...props
}) => {
  let {
    chartType,
    dataPoint,
    description,
    getColor,
    getColumnDisplayName,
    getColumnSortFunction,
    getUnit,
    hoverEffect,
    id,
    isFullScreen,
    isItemHovered,
    legendsPosition,
    onClick,
    onHover,
    onLegendSelect,
    onMouseOut,
    somethingIsHovered,
    sort,
    title,
    xAxisName: xAxisNameConfig,
    xDataKey,
    yAxisName: yAxisNameConfig,
    yAxisRange,
  } = props;

  const dispatch = useDispatch();
  const aggregationFunction = props.aggregationFunction ?? aggregatedSumValue;

  const { invertXAxis, invertYAxis, subDataPoint, xAxis, yAxis } = config;

  if (subDataPoint) dataPoint = subDataPoint;

  const diagramConfig = chartsConfigs[0] ?? {};
  let { direction, partitions, stacks } = diagramConfig;

  if (chartType === chartTypesEnums.LINE || chartType === chartTypesEnums.AREA) stacks = null;

  const isStacked = partitions || stacks;

  const data = withoutIndexData?.map((d, index) => ({
    ...d,
    index,
  }));
  const dataTransformatorDependency = getDataTransformatorMemoDependencyArray({
    config,
  });
  let {
    dataPartitions: preCalculatedPartitions,
    dataStacks: preCalculatedStacks,
    flattenData,
  } = useMemo(() => dataTransformator(data, { ...config }), [dataTransformatorDependency, withoutIndexData]);

  const sortFunction = (a, b, dataKey) => {
    if (getColumnSortFunction) return getColumnSortFunction({ aValue: a, bValue: b, dataKey });
    return defaultSortFunction(a, b);
  };

  const { allAggregatedData, dataStacks, pivotColumnValues, xAxisValues } = useMemo(() => {
    let dataStacks;
    if (!stacks) dataStacks = [];
    else {
      dataStacks = preCalculatedStacks ?? flattenData?.map((e) => e[stacks])?.filter(distinctFilter);
      dataStacks.sort((a, b) => sortFunction(a, b, stacks));
    }
    let dataPartitions;
    if (!partitions) dataPartitions = [];
    else {
      dataPartitions = preCalculatedPartitions ?? flattenData?.map((e) => e[partitions])?.filter(distinctFilter);
      dataPartitions.sort((a, b) => sortFunction(a, b, partitions));
    }

    let allAggregatedData = [];
    const pivotColumn =
      chartType === chartTypesEnums.BAR
        ? partitions && stacks
          ? `(${stacks}) - (${partitions})`
          : partitions || stacks
        : partitions;

    if (partitions && stacks && chartType === chartTypesEnums.BAR) {
      flattenData = flattenData?.map((e) => ({
        ...e,
        [partitions]: null,
        [pivotColumn]: `Stack=${e[stacks]}&Partition=${e[partitions]}`,
        [stacks]: null,
      }));
    }

    if (!isStacked) {
      allAggregatedData = aggregationFunction({
        aggregateColumns: [dataPoint],
        data: flattenData,
        groupByColumns: [xDataKey],
      });
    } else {
      let pivotColumnValuesAll = dataStacks?.map((s) => dataPartitions?.map((p) => `Stack=${s}&Partition=${p}`));
      pivotColumnValuesAll = pivotColumnValuesAll?.reduce((total, current) => [...total, ...current], []);

      var pivotColumnValues =
        pivotColumn === partitions
          ? dataPartitions
          : pivotColumn === stacks
          ? dataStacks
          : pivotColumnValuesAll?.filter((sp) => flattenData?.some((row) => row[pivotColumn] === sp));

      // Remove hardcoded value
      flattenData = aggregationFunction({
        aggregateColumns: [dataPoint],
        data: flattenData,
        groupByColumns: [xDataKey, pivotColumn, "dataPoint"],
      });

      const pivotedData = pivotData(flattenData, pivotColumn, dataPoint);

      allAggregatedData = aggregationFunction({
        aggregateColumns: pivotColumnValues,
        data: pivotedData,
        groupByColumns: [xDataKey],
      });
    }

    // TODO: Remove this static values (Use enums at least)
    let xAxisValues = [];
    if (!sort) sort = "x";

    if ([chartTypesEnums.AREA, chartTypesEnums.BAR, chartTypesEnums.LINE].includes(chartType)) {
      if (sort === "x" || sort === undefined) {
        if (getColumnSortFunction)
          allAggregatedData.sort((a, b) =>
            getColumnSortFunction({
              aValue: a[xDataKey],
              bValue: b[xDataKey],
              dataKey: xDataKey,
            })
          );
        else
          allAggregatedData = sortData({
            data: allAggregatedData,
            dataKey: xDataKey,
          });
      }
      // Remove Hardcoded value
      if (sort === "y" || sort === "All values") {
        if (isStacked)
          allAggregatedData = sortDataAccumulative({
            data: allAggregatedData,
            dataKeys: pivotColumnValues,
          });
        else
          allAggregatedData = sortData({
            data: allAggregatedData,
            dataKey: dataPoint,
          });
      }

      // Remove Hardcoded value
      xAxisValues = xDataKey ? allAggregatedData?.map((row) => row[xDataKey]) : ["Total"];
    }
    if (chartType === chartTypesEnums.LINE) {
      // Remove Hardcoded value
      if (isStacked && sort === "All values") {
        const sorted = {};
        pivotColumnValues.forEach((v) => {
          const pivotValues = allAggregatedData?.map((row) => row[v]);
          pivotValues.sort((a, b) => a - b);
          sorted[v] = pivotValues;
        });
        const sortedAllAggregatedData = [];
        for (const i in allAggregatedData) {
          const sortedIthIndex = {};
          for (const v of pivotColumnValues) {
            sortedIthIndex[v] = sorted[v][i];
          }
          sortedAllAggregatedData.push({ i, ...sortedIthIndex });
        }
        allAggregatedData = sortedAllAggregatedData;
        // Remove Hardcoded value
        xAxisValues = xDataKey ? allAggregatedData?.map((row, index) => index) : ["Total"];
      }
    }

    return {
      allAggregatedData,
      dataPartitions,
      dataStacks,
      pivotColumnValues,
      xAxisValues,
    };
  }, [
    withoutIndexData,
    dataTransformatorDependency,
    dataPoint,
    subDataPoint,
    chartType,
    partitions,
    stacks,
    xDataKey,
    sort,
    props.aggregationFunction,
  ]);

  const isVertical = direction === "vertical";

  const oneToNArray = [...(Array(xAxisValues?.length)?.keys() ?? [])];

  let xAxisOption = !isVertical
    ? {
        axisPointer: {
          type: "shadow",
        },
        data:
          chartType === chartTypesEnums.LINE && sort === "All values"
            ? invertXAxis
              ? reverse(oneToNArray)
              : oneToNArray
            : xAxisValues,
        inverse: invertXAxis,
      }
    : {
        inverse: invertXAxis,
        scale: false,
        type: "value",
      };

  const xUnit = getUnit({ config, quantity: xDataKey });
  const xAxisName =
    xAxisNameConfig ||
    getAxisNameWithUnit({
      dataKey: getColumnDisplayName({ colName: xDataKey }),
      unit: xUnit,
    });

  const dataPointUnit = getUnit({ quantity: dataPoint });
  const yAxisName =
    yAxisNameConfig ||
    getAxisNameWithUnit({
      dataKey: getColumnDisplayName({ colName: dataPoint }),
      unit: dataPointUnit,
    });

  xAxisOption = {
    ...xAxisOption,
    name: !isVertical ? xAxisName : yAxisName,
    nameGap: 25,
    nameLocation: "center",
  };

  let yAxisOption = isVertical
    ? {
        axisPointer: {
          type: "shadow",
        },
        data:
          chartType === chartTypesEnums.LINE && sort === "All values"
            ? invertXAxis
              ? reverse(oneToNArray)
              : oneToNArray
            : xAxisValues,
        inverse: invertYAxis,
      }
    : {
        inverse: invertYAxis,
        name: getColumnDisplayName({ colName: dataPoint }),
        scale: false,
        type: "value",
      };

  yAxisOption = { ...yAxisOption, name: !isVertical ? yAxisName : xAxisName };
  const dataPointColor = getColor
    ? getColor({ chartType, dataPoint, diagrams: chartsConfigs, quantity: dataPoint })
    : undefined;

  let partitionsCounter = -1;

  var option = useMemo(() => {
    return {
      series: !isStacked
        ? [
            {
              areaStyle: chartType === chartTypesEnums.AREA ? {} : undefined,
              color: dataPointColor ?? getItemColor(0),
              data: allAggregatedData?.map((row) => {
                return {
                  itemStyle: {
                    opacity:
                      somethingIsHovered &&
                      isItemHovered &&
                      !isItemHovered({
                        chartType,
                        diagrams: chartsConfigs,
                        row,
                        xAxis,
                        yAxis,
                      })
                        ? 0.05
                        : 1,
                  },
                  row: hoverEffect ? row : {},
                  value: row[dataPoint],
                };
              }),
              maxValue: Math.max(...(allAggregatedData?.map((row) => row[dataPoint] ?? 0) ?? [])),
              name: dataPoint,
              tooltip: {
                valueFormatter: echartsDefaultTooltipFormatter,
              },
              type: chartType === chartTypesEnums.AREA ? chartTypesEnums.LINE : chartType,
            },
          ]
        : pivotColumnValues?.map((value, i) => {
            let partition, stack, valueForRepresentation;
            if (stacks && partitions) {
              partition = value?.split("&Partition=")?.[1];
              stack = value?.split("&Partition=")?.[0]?.replace("Stack=", "");
              valueForRepresentation = `(${stack})-(${partition})`;

              if (i > 0) {
                const previousValue = pivotColumnValues[i - 1];
                const previousStack = previousValue?.split("&Partition=")?.[0]?.replace("Stack=", "");
                if (previousStack !== stack) partitionsCounter = 0;
                else {
                  partitionsCounter++;
                }
              } else {
                partitionsCounter++;
              }
            } else {
              partition = partitions ? value : undefined;
              stack = stacks ? value : undefined;
              valueForRepresentation = value;
            }

            const stackIndex = dataStacks?.findIndex((s) => stack === s);
            const color =
              getColor &&
              getColor({
                chartType,
                dataPoint,
                diagrams: chartsConfigs,
                quantity: valueForRepresentation,
              });
            return {
              areaStyle: chartType === chartTypesEnums.AREA ? {} : undefined,
              color: color
                ? color
                : stacks && partitions
                ? getItemColor(stackIndex, partitionsCounter)
                : getItemColor(i),
              // data: allAggregatedData?.filter((row, i) => i < 10)?.map((row) => {
              data: allAggregatedData?.map((row) => {
                return {
                  itemStyle: {
                    opacity:
                      somethingIsHovered &&
                      isItemHovered &&
                      !isItemHovered({
                        chartType,
                        diagrams: chartsConfigs,
                        row: {
                          ...row,
                          [partitions]: partition,
                          [stacks]: stack,
                        },
                        xAxis,
                        yAxis,
                      })
                        ? 0.05
                        : 1,
                  },
                  row: hoverEffect ? { ...row, [partitions]: partition, [stacks]: stack } : {},
                  value: row[value],
                };
              }),
              emphasis: {
                focus: hoverEffect ? undefined : "series",
              },
              maxValue: max(allAggregatedData?.map((row) => row[value])),
              name: valueForRepresentation,
              stack:
                chartType === chartTypesEnums.LINE ? null : chartType === chartTypesEnums.AREA ? "0" : stack ?? "0",
              tooltip: {
                valueFormatter: echartsDefaultTooltipFormatter,
              },
              type: chartType === chartTypesEnums.AREA ? chartTypesEnums.LINE : chartType,
            };
          }),
      xAxis: [xAxisOption],
      yAxis: [yAxisOption],
    };
  }, [
    somethingIsHovered,
    allAggregatedData,
    dataPoint,
    chartType,
    partitions,
    stacks,
    xAxis,
    yAxis,
    invertYAxis,
    subDataPoint,
    dataPoint,
    isItemHovered,
    invertXAxis,
    pivotColumnValues,
    getColor,
  ]);

  const optionWithAxis = { ...option, xAxis: [xAxisOption], yAxis: [yAxisOption] };

  useEffect(() => {
    dispatch(setDataToDownload({ data: allAggregatedData, id }));
  }, [id, allAggregatedData]);

  const longestY = getArrayLongestElement(
    isVertical ? xAxisValues : optionWithAxis.series?.map((s) => s.maxValue ?? 0)
  );

  return (
    <DynamicEchartsChart
      getLegendDisplayName={(colName) => getColumnDisplayName({ colName })}
      isFullScreen={isFullScreen}
      isVertical={isVertical}
      legendsPosition={legendsPosition}
      longestY={longestY}
      onClick={onClick ? (params) => onClick({ config, params, point: params?.data?.row }) : undefined}
      onHover={onHover ? (params) => onHover({ config, params, point: params?.data?.row }) : undefined}
      onLegendSelect={onLegendSelect ? (params) => onLegendSelect({ config, params }) : undefined}
      onMouseOut={onMouseOut ? (params) => onMouseOut({ config, params }) : undefined}
      option={optionWithAxis}
      title={title}
      tooltip={description}
      {...{ yAxisRange }}
    />
  );
};

const DynamicStackedChartMultithread = ({
  chartsConfigs,
  config,
  data: withoutIndexData,
  dataTransformatorMultithread,
  getDataTransformatorMemoDependencyArray,
  isConfigSetting,
  ...props
}) => {
  let {
    chartType,
    dataPoint,
    description,
    getColor,
    getColumnDisplayName,
    getColumnSortFunction,
    getUnit,
    hoverEffect,
    id,
    isFullScreen,
    isItemHovered,
    legendsPosition,
    onClick,
    onHover,
    onLegendSelect,
    onMouseOut,
    somethingIsHovered,
    sort,
    title,
    xAxisName: xAxisNameConfig,
    xDataKey,
    yAxisName: yAxisNameConfig,
    yAxisRange,
  } = props;
  const dispatch = useDispatch();
  const aggregationFunction = props.aggregationFunction ?? aggregatedSumValue;

  const { invertXAxis, invertYAxis, subDataPoint, xAxis, yAxis } = config;

  if (subDataPoint) dataPoint = subDataPoint;

  const diagramConfig = chartsConfigs[0] ?? {};
  let { direction, partitions, stacks } = diagramConfig;

  if (chartType === chartTypesEnums.LINE || chartType === chartTypesEnums.AREA) stacks = null;

  const isStacked = partitions || stacks;

  const data = withoutIndexData?.map((d, index) => ({
    ...d,
    index,
  }));
  const dataTransformatorDependency = getDataTransformatorMemoDependencyArray({
    config,
  });

  const [flattenData, setFlattenData] = useState([]);
  const [preCalculatedStacks, setPreCalculatedStacks] = useState([]);
  const [preCalculatedPartitions, setPreCalculatedPartitions] = useState([]);

  const {
    dataAggregatorDecorator,
    dataTransformatorDecorator,
    isPainting,
    isProcessing,
    option,
    setIsPainting,
    setOption,
  } = useDynamicDashboardCalculations();

  const activeWorkerRef = useRef(null);
  const workerId = useRef(0);
  useEffect(() => {
    if (activeWorkerRef.current?.terminate) {
      activeWorkerRef.current.terminate();
    }

    workerId.current += 1;

    const workerInstance = dataTransformatorMultithread();
    activeWorkerRef.current = workerInstance;

    const getTransformedData = async () => {
      const {
        dataPartitions: preCalculatedPartitions,
        dataStacks: preCalculatedStacks,
        flattenData: newFlattenData,
        requestId,
      } = await workerInstance.calculate(data, { ...config }, workerId.current);
      if (requestId === workerId.current) {
        setFlattenData(newFlattenData);
        setPreCalculatedStacks(preCalculatedStacks);
        setPreCalculatedPartitions(preCalculatedPartitions);
      }
    };

    if (!data || isConfigSetting) return;
    dataTransformatorDecorator(getTransformedData);
    return () => {
      if (workerInstance.terminate) {
        workerInstance.terminate();
      }
    };
  }, [JSON.stringify(dataTransformatorDependency), withoutIndexData, isConfigSetting]);

  const [allAggregatedData, SetAllAggregatedData] = useState([]);
  const [dataStacks, SetDataStacks] = useState([]);
  const [xAxisValues, SetXAxisValues] = useState([]);
  const [pivotColumnValues, SetPivotColumnValues] = useState([]);

  const sortFunction = (a, b, dataKey) => {
    if (getColumnSortFunction) return getColumnSortFunction({ aValue: a, bValue: b, dataKey });
    return defaultSortFunction(a, b);
  };

  const sortAggregatedDataOnX = (aggregatedData) => {
    const sorted = aggregatedData?.toSorted((a, b) =>
      getColumnSortFunction({
        aValue: a[xDataKey],
        bValue: b[xDataKey],
        dataKey: xDataKey,
      })
    );
    return sorted;
  };

  const sortStacksAndPartitions = (array, colName) => {
    return array?.toSorted((a, b) => sortFunction(a, b, colName));
  };

  const activeAggregaterWorkerRef = useRef(null);
  const aggregaterWorkerId = useRef(0);
  useEffect(() => {
    if (activeAggregaterWorkerRef.current) {
      activeAggregaterWorkerRef.current.terminate();
    }

    aggregaterWorkerId.current += 1;
    const worker = new Worker(new URL("../../../src/stackedChart.worker.js", import.meta.url));
    activeAggregaterWorkerRef.current = worker;

    const runWorker = async (...props) => {
      const stackedChartDataAggregater = Comlink.wrap(worker);
      const result = await stackedChartDataAggregater(...props);
      return result;
    };

    const aggregater = async () => {
      let { allAggregatedDataTemp, dataStacksTemp, pivotColumnValuesTemp, requestId, xAxisValuesTemp } =
        await runWorker(
          {
            chartType,
            dataPoint,
            flattenData,
            isStacked,
            partitions,
            preCalculatedPartitions,
            preCalculatedStacks,
            sort,
            stacks,
            xDataKey,
          },
          getColumnSortFunction ? Comlink.proxy(sortAggregatedDataOnX) : undefined,
          Comlink.proxy(sortStacksAndPartitions),
          aggregaterWorkerId.current,
          Comlink.proxy(aggregationFunction)
        );

      if (requestId === aggregaterWorkerId.current) {
        SetAllAggregatedData(allAggregatedDataTemp);
        SetDataStacks(dataStacksTemp);
        SetXAxisValues(xAxisValuesTemp);
        SetPivotColumnValues(pivotColumnValuesTemp);
      }
    };

    if (!flattenData || isConfigSetting) return;
    dataAggregatorDecorator(aggregater, worker);
    return () => {
      worker.terminate();
    };
  }, [
    withoutIndexData,
    dataTransformatorDependency,
    dataPoint,
    subDataPoint,
    chartType,
    partitions,
    stacks,
    xDataKey,
    sort,
    flattenData,
    isConfigSetting,
    props.aggregationFunction,
  ]);

  const isVertical = direction === "vertical";

  const oneToNArray = [...(Array(xAxisValues?.length)?.keys() ?? [])];

  let xAxisOption = !isVertical
    ? {
        axisPointer: {
          type: "shadow",
        },
        data:
          chartType === chartTypesEnums.LINE && sort === "All values"
            ? invertXAxis
              ? reverse(oneToNArray)
              : oneToNArray
            : xAxisValues,
        inverse: invertXAxis,
      }
    : {
        inverse: invertXAxis,
        scale: false,
        type: "value",
      };

  const xUnit = getUnit({ config, quantity: xDataKey });
  const xAxisName =
    xAxisNameConfig ||
    getAxisNameWithUnit({
      dataKey: getColumnDisplayName({ colName: xDataKey }),
      unit: xUnit,
    });

  const dataPointUnit = getUnit({ quantity: dataPoint });
  const yAxisName =
    yAxisNameConfig ||
    getAxisNameWithUnit({
      dataKey: getColumnDisplayName({ colName: dataPoint }),
      unit: dataPointUnit,
    });

  xAxisOption = {
    ...xAxisOption,
    name: !isVertical ? xAxisName : yAxisName,
    nameGap: 25,
    nameLocation: "center",
  };

  let yAxisOption = isVertical
    ? {
        axisPointer: {
          type: "shadow",
        },
        data:
          chartType === chartTypesEnums.LINE && sort === "All values"
            ? invertXAxis
              ? reverse(oneToNArray)
              : oneToNArray
            : xAxisValues,
        inverse: invertYAxis,
      }
    : {
        inverse: invertYAxis,
        name: getColumnDisplayName({ colName: dataPoint }),
        scale: false,
        type: "value",
      };

  yAxisOption = { ...yAxisOption, name: !isVertical ? yAxisName : xAxisName };
  const dataPointColor = getColor
    ? getColor({ chartType, dataPoint, diagrams: chartsConfigs, quantity: dataPoint })
    : undefined;

  let partitionsCounter = -1;

  useEffect(() => {
    if (isConfigSetting || !flattenData) return;
    const newOptions = {
      series: !isStacked
        ? [
            {
              areaStyle: chartType === chartTypesEnums.AREA ? {} : undefined,
              color: dataPointColor ?? getItemColor(0),
              data: allAggregatedData?.map((row) => {
                return {
                  itemStyle: {
                    opacity:
                      somethingIsHovered &&
                      isItemHovered &&
                      !isItemHovered({
                        chartType,
                        diagrams: chartsConfigs,
                        row,
                        xAxis,
                        yAxis,
                      })
                        ? 0.05
                        : 1,
                  },
                  row: hoverEffect ? row : {},
                  value: row[dataPoint],
                };
              }),
              maxValue: Math.max(...(allAggregatedData?.map((row) => row[dataPoint] ?? 0) ?? [])),
              name: dataPoint,
              tooltip: {
                valueFormatter: echartsDefaultTooltipFormatter,
              },
              type: chartType === chartTypesEnums.AREA ? chartTypesEnums.LINE : chartType,
            },
          ]
        : pivotColumnValues?.map((value, i) => {
            let partition, stack, valueForRepresentation;
            if (stacks && partitions) {
              partition = value?.split("&Partition=")?.[1];
              stack = value?.split("&Partition=")?.[0]?.replace("Stack=", "");
              valueForRepresentation = `(${stack})-(${partition})`;

              if (i > 0) {
                const previousValue = pivotColumnValues[i - 1];
                const previousStack = previousValue?.split("&Partition=")?.[0]?.replace("Stack=", "");
                if (previousStack !== stack) partitionsCounter = 0;
                else {
                  partitionsCounter++;
                }
              } else {
                partitionsCounter++;
              }
            } else {
              partition = partitions ? value : undefined;
              stack = stacks ? value : undefined;
              valueForRepresentation = value;
            }

            const stackIndex = dataStacks?.findIndex((s) => stack === s);
            const color =
              getColor &&
              getColor({
                chartType,
                dataPoint,
                diagrams: chartsConfigs,
                quantity: valueForRepresentation,
              });
            return {
              areaStyle: chartType === chartTypesEnums.AREA ? {} : undefined,
              color: color
                ? color
                : stacks && partitions
                ? getItemColor(stackIndex, partitionsCounter)
                : getItemColor(i),
              // data: allAggregatedData?.filter((row, i) => i < 10)?.map((row) => {
              data: allAggregatedData?.map((row) => {
                return {
                  itemStyle: {
                    opacity:
                      somethingIsHovered &&
                      isItemHovered &&
                      !isItemHovered({
                        chartType,
                        diagrams: chartsConfigs,
                        row: {
                          ...row,
                          [partitions]: partition,
                          [stacks]: stack,
                        },
                        xAxis,
                        yAxis,
                      })
                        ? 0.05
                        : 1,
                  },
                  row: hoverEffect ? { ...row, [partitions]: partition, [stacks]: stack } : {},
                  value: row[value],
                };
              }),
              emphasis: {
                focus: hoverEffect ? undefined : "series",
              },
              // 35 -  12 - 8
              // sampling: 'lttb',
              large: true,
              largeThreshold: 1000,
              maxValue: max(allAggregatedData?.map((row) => row[value])),
              name: valueForRepresentation,
              progressive: 500,
              progressiveThreshold: 1000,
              stack:
                chartType === chartTypesEnums.LINE ? null : chartType === chartTypesEnums.AREA ? "0" : stack ?? "0",
              tooltip: {
                valueFormatter: echartsDefaultTooltipFormatter,
              },
              type: chartType === chartTypesEnums.AREA ? chartTypesEnums.LINE : chartType,
            };
          }),
    };
    setOption({ ...newOptions });
  }, [
    somethingIsHovered,
    allAggregatedData,
    dataPoint,
    chartType,
    partitions,
    stacks,
    xAxis,
    yAxis,
    subDataPoint,
    dataPoint,
    pivotColumnValues,
    getColor,
    isConfigSetting,
    isItemHovered,
  ]);

  const optionWithAxis = { ...option, xAxis: [xAxisOption], yAxis: [yAxisOption] };

  useEffect(() => {
    dispatch(setDataToDownload({ data: allAggregatedData, id }));
  }, [id, allAggregatedData]);

  const longestY = getArrayLongestElement(
    isVertical ? xAxisValues : optionWithAxis.series?.map((s) => s.maxValue ?? 0)
  );

  if (isConfigSetting) return;
  return (
    <DynamicEchartsChartWithLoader
      getLegendDisplayName={(colName) => getColumnDisplayName({ colName })}
      isFullScreen={isFullScreen}
      isPainting={isPainting}
      isProcessing={isProcessing}
      isVertical={isVertical}
      legendsPosition={legendsPosition}
      longestY={longestY}
      onClick={onClick ? (params) => onClick({ config, params, point: params?.data?.row }) : undefined}
      onFinished={() => {
        setIsPainting(false);
      }}
      onHover={onHover ? (params) => onHover({ config, params, point: params?.data?.row }) : undefined}
      onLegendSelect={onLegendSelect ? (params) => onLegendSelect({ config, params }) : undefined}
      onMouseOut={onMouseOut ? (params) => onMouseOut({ config, params }) : undefined}
      option={optionWithAxis}
      setIsPainting={setIsPainting}
      title={title}
      tooltip={description}
      {...{ yAxisRange }}
    />
  );
};

const ChartLoader = ({ className, isCalculating }) => {
  const { t } = useTranslation();
  const message = isCalculating ? t("general.processingData") : t("general.painting");

  return (
    <div className={className}>
      <Stack className="font-ds-headline-md w-full gap-1">
        <Icon className="icon-opacity" iconName={isCalculating ? "WrenchScrewDriver" : "PaintBrush"} />
        <p className="">{message}...</p>
      </Stack>
    </div>
  );
};

const DynamicEchartsChartWithLoader = ({ isPainting, isProcessing, setIsPainting, ...echartProps }) => {
  return (
    <div className="relative h-full w-full">
      {isPainting && <ChartLoader className="absolute left-1 top-1  w-full" />}
      {isProcessing && !isPainting && <ChartLoader className="absolute left-1 top-1  w-full" isCalculating={true} />}
      {!isProcessing && (
        <DynamicEchartsChart
          {...echartProps}
          onFinished={() => {
            setIsPainting(false);
          }}
        />
      )}
    </div>
  );
};

const DynamicHeatmap = ({
  chartsConfigs,
  config,
  data,
  dataTransformator,
  description,
  getColor,
  getColumnDisplayName,
  getDataTransformatorMemoDependencyArray,
  getUnit,
  id,
  isItemHovered,
  onClick,
  onHover,
  onLegendSelect,
  onMouseOut,
  somethingIsHovered,
  title,
  xAxisName: xAxisNameConfig,
  xDataKey,
  yAxisName: yAxisNameConfig,
  yDataKey,
  ...props
}) => {
  const dispatch = useDispatch();
  let dataPoint = props.dataPoint;
  const aggregationFunction = props.aggregationFunction ?? aggregatedSumValue;

  let { chartType, invertXAxis, invertYAxis, subDataPoint, xAxis, yAxis } = config;
  if (subDataPoint) dataPoint = subDataPoint;

  const dataTransformatorDependency = getDataTransformatorMemoDependencyArray({
    config,
  });
  let { flattenData, xLabels, yLabels } = useMemo(
    () => dataTransformator(data, { ...config }),
    [dataTransformatorDependency, data]
  );

  const { aggregatedData, xLabelsMemoized, xyzData, yLabelsMemoized } = useMemo(() => {
    const aggregatedData = aggregationFunction({
      aggregateColumns: [dataPoint],
      data: flattenData,
      groupByColumns: [xDataKey, yDataKey],
    });

    const xyzData = [];
    yLabels.forEach((y, yIndex) => {
      xLabels.forEach((x, xIndex) => {
        const xyRecordIndex = aggregatedData.findIndex((r) => r[xDataKey] === x && r[yDataKey] === y);
        const z = xyRecordIndex === -1 ? 0 : aggregatedData[xyRecordIndex][dataPoint];
        xyzData.push([xIndex, yIndex, z || 0]);
      });
    });

    return {
      aggregatedData,
      xLabelsMemoized: xLabels,
      xyzData,
      yLabelsMemoized: yLabels,
    };
  }, [data, dataTransformatorDependency, dataPoint, props.aggregationFunction, subDataPoint, xDataKey, yDataKey]);

  const defaultColor = "var(--color-element-primary-initial)";
  const color = getColor && getColor({ chartType, dataPoint, diagrams: chartsConfigs, quantity: dataPoint });
  const dataPointUnit = getUnit({ config, quantity: dataPoint });

  const xLabelsMemoizedDisplayName = xLabels?.map((x) => getColumnDisplayName({ colName: x }));
  const yLabelsMemoizedDisplayName = yLabels?.map((y) => getColumnDisplayName({ colName: y }));

  var option = useMemo(() => {
    if (!isNumeric(Math.min(...(aggregatedData?.map((row) => row[dataPoint]) ?? [])))) return {};

    return {
      grid: {
        bottom: 60,
        containLabel: true,
        right: 70,
        top: 40,
      },
      legend: {
        bottom: 0,
        formatter: (name) =>
          getAxisNameWithUnit({
            dataKey: getColumnDisplayName({ colName: name }),
            unit: dataPointUnit,
          }),
        itemStyle: {
          color: color ?? defaultColor,
        },
        left: 10,
        right: 40,
        show: true,
      },
      series: [
        {
          data: xyzData.map((xyz) => {
            const xLabel = xLabelsMemoized?.[xyz?.[0]];
            const yLabel = yLabelsMemoized?.[xyz?.[1]];
            return {
              itemStyle: {
                opacity:
                  somethingIsHovered &&
                  isItemHovered &&
                  !isItemHovered({
                    chartType,
                    diagrams: chartsConfigs,
                    row: { [xDataKey]: xLabel, [yDataKey]: yLabel },
                    xAxis,
                    yAxis,
                  })
                    ? 0
                    : 1,
              },
              row: { [xDataKey]: xLabel, [yDataKey]: yLabel },
              value: xyz,
            };
          }),
          emphasis: {
            // itemStyle: {
            //   shadowBlur: 10,
            // },
          },
          label: {
            show: false,
          },
          name: dataPoint,
          tooltip: {
            valueFormatter: echartsDefaultTooltipFormatter,
          },
          type: "heatmap",
        },
      ],
      tooltip: {
        position: "top",
      },
      visualMap: {
        calculable: true,
        inRange: {
          color: ["var(--color-surface-bright)", color ?? defaultColor],
        },
        left: "right",
        max: Math.max(...(aggregatedData?.map((row) => row[dataPoint]) ?? [])),
        min: Math.min(...(aggregatedData?.map((row) => row[dataPoint]) ?? [])),
        orient: "vertical",
        top: 40,
      },
    };
  }, [somethingIsHovered, aggregatedData, dataPoint, subDataPoint, xAxis, isItemHovered, yAxis, getColor, config]);

  const optionWithAxis = {
    ...option,
    xAxis: [
      {
        boundaryGap: true,
        data: xLabelsMemoizedDisplayName,
        inverse: invertXAxis,
        name: xAxisNameConfig || getColumnDisplayName({ colName: xDataKey }),
        nameGap: 23,
        nameLocation: "center",
        splitArea: {
          show: true,
        },
      },
    ],
    yAxis: [
      {
        boundaryGap: true,
        data: yLabelsMemoizedDisplayName,
        inverse: invertYAxis,
        name: yAxisNameConfig || getColumnDisplayName({ colName: yDataKey }),
        splitArea: {
          show: true,
        },
      },
    ],
  };

  useEffect(() => {
    dispatch(setDataToDownload({ data: aggregatedData, id }));
  }, [id, aggregatedData, dispatch]);

  const longestY = getArrayLongestElement(option?.yAxis?.[0]?.data);
  if (option?.series?.[0]?.data?.length)
    return (
      <DynamicEchartsChart
        getLegendDisplayName={(colName) => getColumnDisplayName({ colName })}
        longestY={longestY}
        onClick={onClick ? (params) => onClick({ config, params, point: params?.data?.row }) : undefined}
        onHover={onHover ? (params) => onHover({ config, params, point: params?.data?.row }) : undefined}
        onLegendSelect={onLegendSelect ? (params) => onLegendSelect({ config, params }) : undefined}
        onMouseOut={onMouseOut ? (params) => onMouseOut({ config, params }) : undefined}
        option={optionWithAxis}
        title={title}
        tooltip={description}
      />
    );
};

const DynamicHeatmapMultithread = ({
  chartsConfigs,
  config,
  data,
  dataTransformatorMultithread,
  description,
  getColor,
  getColumnDisplayName,
  getDataTransformatorMemoDependencyArray,
  getUnit,
  id,
  isConfigSetting,
  isItemHovered,
  onClick,
  onHover,
  onLegendSelect,
  onMouseOut,
  somethingIsHovered,
  title,
  xAxisName: xAxisNameConfig,
  xDataKey,
  yAxisName: yAxisNameConfig,
  yDataKey,
  ...props
}) => {
  const aggregationFunction = props.aggregationFunction ?? aggregatedSumValue;

  const dispatch = useDispatch();
  let dataPoint = props.dataPoint;

  let { chartType, invertXAxis, invertYAxis, subDataPoint, xAxis, yAxis } = config;
  if (subDataPoint) dataPoint = subDataPoint;

  const dataTransformatorDependency = getDataTransformatorMemoDependencyArray({
    config,
  });

  const [flattenData, setFlattenData] = useState([]);
  const [xLabels, setXLabels] = useState([]);
  const [yLabels, setYLabels] = useState([]);

  const {
    dataAggregatorDecorator,
    dataTransformatorDecorator,
    isPainting,
    isProcessing,
    option,
    setIsPainting,
    setOption,
  } = useDynamicDashboardCalculations();

  const activeWorkerRef = useRef(null);
  const workerId = useRef(0);
  useEffect(() => {
    if (activeWorkerRef.current?.terminate) {
      activeWorkerRef.current.terminate();
    }

    workerId.current += 1;

    const workerInstance = dataTransformatorMultithread();
    activeWorkerRef.current = workerInstance;

    const getTransformedData = async () => {
      const {
        flattenData: newFlattenData,
        requestId,
        xLabels,
        yLabels,
      } = await workerInstance.calculate(data, { ...config }, workerId.current);
      if (requestId === workerId.current) {
        setFlattenData(newFlattenData);
        setXLabels(xLabels);
        setYLabels(yLabels);
      }
    };

    if (!data || isConfigSetting) return;
    dataTransformatorDecorator(getTransformedData);

    return () => {
      if (workerInstance.terminate) {
        workerInstance.terminate();
      }
    };
  }, [JSON.stringify(dataTransformatorDependency), data, isConfigSetting]);

  const [allAggregatedData, SetAllAggregatedData] = useState([]);
  const [xyzData, SetXYZData] = useState([]);

  const activeAggregaterWorkerRef = useRef(null);
  const aggregaterWorkerId = useRef(0);
  useEffect(() => {
    if (activeAggregaterWorkerRef.current) {
      activeAggregaterWorkerRef.current.terminate();
    }

    aggregaterWorkerId.current += 1;
    const worker = new Worker(new URL("../../../src/heatmapChart.worker.js", import.meta.url));
    activeAggregaterWorkerRef.current = worker;
    const runWorker = async (...props) => {
      const heatmapChartDataAggregater = Comlink.wrap(worker);
      const result = await heatmapChartDataAggregater(...props);
      return result;
    };

    const aggregater = async () => {
      let { allAggregatedData, requestId, xyzData } = await runWorker(
        {
          dataPoint,
          flattenData,
          xDataKey,
          xLabels,
          yDataKey,
          yLabels,
        },
        aggregaterWorkerId.current,
        Comlink.proxy(aggregationFunction)
      );

      if (requestId === aggregaterWorkerId.current) {
        SetAllAggregatedData(allAggregatedData);
        SetXYZData(xyzData);
      }
    };

    if (!flattenData || isConfigSetting) return;
    dataAggregatorDecorator(aggregater, worker);

    return () => {
      worker.terminate();
    };
  }, [
    data,
    dataTransformatorDependency,
    dataPoint,
    subDataPoint,
    xDataKey,
    flattenData,
    isConfigSetting,
    props.aggregationFunction,
  ]);

  const defaultColor = "var(--color-element-primary-initial)";
  const color = getColor && getColor({ chartType, dataPoint, diagrams: chartsConfigs, quantity: dataPoint });
  const dataPointUnit = getUnit({ config, quantity: dataPoint });

  const xLabelsMemoizedDisplayName = xLabels?.map((x) => getColumnDisplayName({ colName: x }));
  const yLabelsMemoizedDisplayName = yLabels?.map((y) => getColumnDisplayName({ colName: y }));

  useEffect(() => {
    if (isConfigSetting || !flattenData || !xyzData) return;

    let newOptions = {};
    if (isNumeric(Math.min(...(allAggregatedData?.map((row) => row[dataPoint]) ?? []))))
      newOptions = {
        grid: {
          bottom: 60,
          containLabel: true,
          right: 70,
          top: 40,
        },
        legend: {
          bottom: 0,
          formatter: (name) =>
            getAxisNameWithUnit({
              dataKey: getColumnDisplayName({ colName: name }),
              unit: dataPointUnit,
            }),
          itemStyle: {
            color: color ?? defaultColor,
          },
          left: 10,
          right: 40,
          show: true,
        },
        series: [
          {
            data: xyzData.map((xyz) => {
              const xLabel = xLabels?.[xyz?.[0]];
              const yLabel = yLabels?.[xyz?.[1]];
              return {
                itemStyle: {
                  opacity:
                    somethingIsHovered &&
                    isItemHovered &&
                    !isItemHovered({
                      chartType,
                      diagrams: chartsConfigs,
                      row: { [xDataKey]: xLabel, [yDataKey]: yLabel },
                      xAxis,
                      yAxis,
                    })
                      ? 0
                      : 1,
                },
                row: { [xDataKey]: xLabel, [yDataKey]: yLabel },
                value: xyz,
              };
            }),
            emphasis: {
              // itemStyle: {
              //   shadowBlur: 10,
              // },
            },
            label: {
              show: false,
            },
            name: dataPoint,
            tooltip: {
              valueFormatter: echartsDefaultTooltipFormatter,
            },
            type: "heatmap",
          },
        ],
        tooltip: {
          position: "top",
        },
        visualMap: {
          calculable: true,
          inRange: {
            color: ["var(--color-surface-bright)", color ?? defaultColor],
          },
          left: "right",
          max: Math.max(...(allAggregatedData?.map((row) => row[dataPoint]) ?? [])),
          min: Math.min(...(allAggregatedData?.map((row) => row[dataPoint]) ?? [])),
          orient: "vertical",
          top: 40,
        },
      };
    setOption({ ...newOptions });
  }, [
    somethingIsHovered,
    allAggregatedData,
    xDataKey,
    yDataKey,
    xLabels,
    yLabels,
    dataPoint,
    xAxis,
    yAxis,
    isItemHovered,
    subDataPoint,
    getColor,
    isConfigSetting,
  ]);

  const optionWithAxis = {
    ...option,
    xAxis: [
      {
        boundaryGap: true,
        data: xLabelsMemoizedDisplayName,
        inverse: invertXAxis,
        name: xAxisNameConfig || getColumnDisplayName({ colName: xDataKey }),
        nameGap: 23,
        nameLocation: "center",
        splitArea: {
          show: true,
        },
      },
    ],
    yAxis: [
      {
        boundaryGap: true,
        data: yLabelsMemoizedDisplayName,
        inverse: invertYAxis,
        name: yAxisNameConfig || getColumnDisplayName({ colName: yDataKey }),
        splitArea: {
          show: true,
        },
      },
    ],
  };

  useEffect(() => {
    dispatch(setDataToDownload({ data: allAggregatedData, id }));
  }, [id, allAggregatedData]);
  const longestY = getArrayLongestElement(option?.yAxis?.[0]?.data);

  if (isConfigSetting) return;
  return (
    <DynamicEchartsChartWithLoader
      getLegendDisplayName={(colName) => getColumnDisplayName({ colName })}
      isPainting={isPainting}
      isProcessing={isProcessing}
      longestY={longestY}
      onClick={onClick ? (params) => onClick({ config, params, point: params?.data?.row }) : undefined}
      onFinished={() => {
        setIsPainting(false);
      }}
      onHover={onHover ? (params) => onHover({ config, params, point: params?.data?.row }) : undefined}
      onLegendSelect={onLegendSelect ? (params) => onLegendSelect({ config, params }) : undefined}
      onMouseOut={onMouseOut ? (params) => onMouseOut({ config, params }) : undefined}
      option={optionWithAxis}
      setIsPainting={setIsPainting}
      title={title}
      tooltip={description}
    />
  );
};

const DynamicMiniMap = ({
  appName,
  config,
  data,
  dataPoint,
  dataTransformator,
  description,
  eventHandlers,
  getColor,
  getColumnDisplayName,
  getColumnSortFunction,
  getDataTransformatorMemoDependencyArray,
  getUnit,
  id,
  IdColumnName,
  isItemHovered,
  miniMapObjectsNameKey,
  somethingIsHovered,
  title,
  ...props
}) => {
  const aggregationFunction = props.aggregationFunction ?? aggregatedSumValue;

  const dispatch = useDispatch();
  const { chartType } = config;

  const dataTransformatorDependency = getDataTransformatorMemoDependencyArray({
    config,
  });
  let { flattenData: transformedData } = useMemo(
    () => dataTransformator(data, { ...config }),
    [dataTransformatorDependency, data]
  );

  const isDataPointcategorical = transformedData?.some((row) => typeof row[dataPoint] === "string");

  const strategy = !isDataPointcategorical
    ? createMiniMapNumericalStrategy({
        aggregationFunction,
        appName,
        chartType,
        config,
        data: transformedData,
        dataPoint,
        IdColumnName,
        isItemHovered,
        miniMapObjectsNameKey,
        somethingIsHovered,
      })
    : createMiniMapCategoricalStrategy({
        chartType,
        config,
        data: transformedData,
        dataPoint,
        getColor,
        getColumnSortFunction,
        IdColumnName,
        isItemHovered,
        miniMapObjectsNameKey,
        onLegendSelect: props?.onLegendSelect,
        somethingIsHovered,
      });

  const { colorBarProps, flattenData, miniMapData } = strategy.useLogic();

  const dataPointUnit = getUnit({ config, quantity: dataPoint });
  const dataPointUnitText = getAxisNameWithUnit({ unit: dataPointUnit });

  const screenshotRef = useRef();

  useEffect(() => {
    const dataToDownload = miniMapData?.map((point) => {
      return {
        center: point.center,
        color: point.color,
        [dataPoint]: point.value,
      };
    });
    dispatch(setDataToDownload({ data: dataToDownload, id }));
  }, [id, miniMapData]);

  const eventHandlersMemoized = useCallback(eventHandlers(flattenData, config), [
    JSON.stringify([flattenData, config]),
  ]);

  return (
    <div className="h-full w-full">
      <Stack className="relative h-full w-full" flexDirection="column" ref={screenshotRef}>
        <DynamicChartTitle
          title={title || getColumnDisplayName({ colName: dataPoint }) + dataPointUnitText}
          tooltip={description}
        />
        <div className="relative w-full flex-1">
          {/* Pass key to the map for force rezooming when only data gets updated */}
          <MyMap
            data={miniMapData ?? []}
            eventHandlers={eventHandlersMemoized}
            key={flattenData}
            rerenderVariable={JSON.stringify([flattenData, dataPoint])}
            resizeTriggerVariables={JSON.stringify([config.w, config.h])}
            shouldCluster={!somethingIsHovered}
          />
          <strategy.ColorBar className=" absolute right-2 z-[999]" {...colorBarProps} />
        </div>
      </Stack>
    </div>
  );
};

const DynamicSummary = ({
  config,
  data: withoutIndexData,
  dataTransformator,
  description,
  getColor,
  getColumnDisplayName,
  getDataTransformatorMemoDependencyArray,
  isItemHovered,
  ...props
}) => {
  const aggregationFunction = props.aggregationFunction;

  let { chartType, dataPoint, height, id, title } = props;
  const dispatch = useDispatch();

  const data = withoutIndexData?.map((d, index) => ({
    ...d,
    index,
  }));
  const dataTransformatorDependency = getDataTransformatorMemoDependencyArray({
    config,
  });
  let { flattenData } = useMemo(
    () => dataTransformator(data, { ...config }),
    [dataTransformatorDependency, withoutIndexData]
  );

  let { dataPartitions, kpiAggregatedDataMemoized } = useMemo(() => {
    const aggregatedData = aggregationFunction?.({
      aggregateColumns: [],
      data: flattenData,
      groupByColumns: [],
    });
    return {
      dataPartitions: Object.keys(aggregatedData ?? {}),
      kpiAggregatedDataMemoized: aggregatedData,
    };
  }, [withoutIndexData, dataTransformatorDependency, dataPoint, props.aggregationFunction]);

  useEffect(() => {
    dispatch(setDataToDownload({ data: [kpiAggregatedDataMemoized], id: id }));
  }, [kpiAggregatedDataMemoized, id, dispatch]);

  const configsCalculationsMemoized = useMemo(() => {
    let configsCalculations = {};

    dataPartitions?.forEach((p, j) => {
      const color = "var(--color-foreground-secondary)";
      const isTransparent = false;

      configsCalculations[p] = {
        color: color ?? getItemColor(0, j),
        isTransparent,
        value: kpiAggregatedDataMemoized?.[p],
      };
    });

    return configsCalculations;
  }, [withoutIndexData, dataPoint, isItemHovered, dataPartitions, getColor, kpiAggregatedDataMemoized, chartType]);
  const { className } = useDelayedTransitionedDisplay();

  const somethingIsHovered = false;

  return (
    <>
      <DynamicChartTitle className={className} title={title} tooltip={description} />
      <Stack
        className={cn("w-full overflow-x-auto overflow-y-auto px-4", className)}
        flexDirection="column"
        height={height - 48}
      >
        {dataPartitions?.map((p) => {
          const value = configsCalculationsMemoized?.[p]?.value;
          const color = configsCalculationsMemoized?.[p]?.color;
          const isTransparent = configsCalculationsMemoized?.[p]?.isTransparent;

          return (
            <LegendItemWithValue
              bulletHeight="1.2rem"
              bulletType="circle"
              bulletWidth="1.2rem"
              className={`w-full py-1  ${isTransparent ? "opacity-[0.2]" : "opacity-[1]"}
              ${somethingIsHovered && !isTransparent && "bg-lds-selection-subtle"}`}
              color={color}
              direction="row"
              hasBullet={true}
              key={p}
              label={getColumnDisplayName({ colName: p })}
              value={value}
            />
          );
        })}
        <Legend />
        <Tooltip />
      </Stack>
    </>
  );
};

const ChartType = ({ chartType, multithread, xDataKey = "", ...props }) => {
  if (!props.chartsConfigs?.length) return;
  if (chartType === chartTypesEnums.COMPOSED) {
    if (!props?.data?.length || !xDataKey) return;
    return multithread ? (
      <DynamicComboChartMultithread {...{ xDataKey, ...props }} />
    ) : (
      <DynamicComboChart {...{ xDataKey, ...props }} />
    );
  }

  if (chartType === chartTypesEnums.SCATTER) {
    if (!props?.data?.length || !xDataKey) return;
    return multithread ? (
      <DynamicScatterChartMultithread {...{ xDataKey, ...props }} />
    ) : (
      <DynamicScatterChart {...{ xDataKey, ...props }} />
    );
  }

  if (chartType === chartTypesEnums.HISTOGRAM) {
    if (!props?.chartsConfigs?.filter((k) => k.dataKey)?.length) return;
    return <DynamicHistogramChart {...{ ...props }} />;
  }

  if ([chartTypesEnums.DOUGHNUT, chartTypesEnums.PIE].includes(chartType)) {
    if (!props?.chartsConfigs.length) return;
    return multithread ? (
      <DynamicPieChartMultithread {...{ ...props, chartType }} />
    ) : (
      <DynamicPieChart {...{ ...props, chartType }} />
    );
  }

  if ([chartTypesEnums.AREA, chartTypesEnums.BAR, chartTypesEnums.LINE].includes(chartType))
    return multithread ? (
      <DynamicStackedChartMultithread {...{ ...props, chartType, xDataKey }} />
    ) : (
      <DynamicStackedChart {...{ ...props, chartType, xDataKey }} />
    );

  if (chartType === chartTypesEnums.KPI)
    return multithread ? (
      <DynamicKPIMultithread {...{ ...props, chartType, xDataKey }} />
    ) : (
      <DynamicKPI {...{ ...props, chartType, xDataKey }} />
    );

  //TODO: dataPoint change doesn't rerender the heatmap? Why error?
  if (chartType === chartTypesEnums.HEATMAP && props.dataPoint) {
    if (!props?.dataPoint || !xDataKey || !props?.yDataKey) return;
    return multithread ? (
      <DynamicHeatmapMultithread {...{ xDataKey, ...props }} />
    ) : (
      <DynamicHeatmap {...{ xDataKey, ...props }} />
    );
  }
  if (chartType === chartTypesEnums.MINI_MAP && props.dataPoint) {
    return <DynamicMiniMap {...{ xDataKey, ...props }} />;
  }
  if (chartType === chartTypesEnums.SUMMARY) {
    return <DynamicSummary {...{ ...props }} />;
  }
};

const DynamicChartSettings = ({ diagrams, selectedChartType, setDiagrams, settings }) => {
  const { t } = useTranslation();
  const handleAddDiagram = () => {
    setDiagrams([...diagrams, diagramInitialConfig]);
  };

  const diagramsSettings = settings?.filter((setting) => setting.isInDiagramsPart);
  const otherSettings = settings?.filter((setting) => !setting.isInDiagramsPart);
  const hasMultipleDiagrams = [chartTypesEnums.COMPOSED, chartTypesEnums.HISTOGRAM, chartTypesEnums.SCATTER].includes(
    selectedChartType
  );

  return (
    <Stack className="mt-4 w-full gap-2" direction="column">
      {otherSettings?.map((setting) => {
        if (!setting.visible()) return;
        const { filterType, ...args } = setting;
        const filterFactory = new DynamicFilterFactory(filterType, args);
        const filterInstance = filterFactory.createFilterClassInstance();
        if (filterInstance) {
          const FilterComponent = filterInstance.createComponent();
          return (
            <Stack className="w-full items-start" justifyContent="space-between" key={setting.filterName}>
              <span className="font-ds-headline-md block w-1/3">{setting.filterLabel} :</span>
              {FilterComponent}
            </Stack>
          );
        }
      })}
      {diagrams?.map((diagram, index) => {
        return (
          <>
            {hasMultipleDiagrams && (
              <Stack className="mt-1 items-center" gap={2}>
                <Icon
                  color="var(--color-purpose-status-negative)"
                  iconName="Close"
                  onClick={() => setDiagrams(diagrams.filter((_d, i) => i !== index))}
                  size="sm"
                />
                <span className="font-ds-headline-md block">Diagram {index + 1}:</span>
              </Stack>
            )}
            {diagramsSettings?.map((setting) => {
              if (!setting.visible(index)) return;
              setting.config = diagram;
              setting.setConfig = setting.setConfigFn(index);

              const { filterType, ...args } = setting;
              const filterFactory = new DynamicFilterFactory(filterType, args);
              const filterInstance = filterFactory.createFilterClassInstance();
              if (filterInstance) {
                const FilterComponent = filterInstance.createComponent();
                return (
                  <Stack className="w-full items-start" justifyContent="space-between" key={setting.filterName}>
                    <span className="font-ds-headline-md block w-1/3">{setting.filterLabel} :</span>
                    {FilterComponent}
                  </Stack>
                );
              }
            })}
          </>
        );
      })}
      {hasMultipleDiagrams && (
        <Button className="mt-1" onClick={handleAddDiagram} size="sm" variant="primary">
          {plus} {t("EnergyPerformance.dashboard.addNewDiagram")}
        </Button>
      )}
    </Stack>
  );
};

const ChartNavbar = ({
  captureTargetElement,
  enterFullScreenMode,
  exitFullScreenMode,
  handleClick,
  hasFilters,
  id,
  isFullScreen,
  isVisible,
  onClose,
  setChartSelectionIsOpen,
  setIsVisible,
}) => {
  const configsIconsColor = "var(--color-element-primary-initial)";
  const items = [
    {
      color: "var(--color-purpose-status-negative-subtle)",
      iconName: "Trash",
      onClick: onClose,
    },
    {
      iconName: isFullScreen ? "FullscreenExit" : "Fullscreen",
      onClick: isFullScreen ? exitFullScreenMode : enterFullScreenMode,
      stroke: true,
    },
    {
      iconName: isVisible ? "EyeClose" : "EyeOpen",
      onClick: () => setIsVisible(!isVisible),
    },
    {
      iconName: "Settings",
      onClick: () => handleClick("settings"),
    },
    {
      iconName: "Filter",
      inactive: !hasFilters,
      onClick: () => handleClick("filters"),
    },
    {
      iconName: "Piechart",
      onClick: () => {
        setChartSelectionIsOpen((chartSelectionIsOpen) => !chartSelectionIsOpen);
      },
    },
  ];

  const dataToDownload = useSelector((store) => store.energyPerformanceNew).dataToDownload?.[id];

  const svgClassName = "w-4 h-4 transition";
  return (
    <Stack
      className="h-6 w-full cursor-grab items-center gap-4 rounded bg-lds-selection-subtle px-2 "
      flexDirection="row-reverse"
    >
      {items?.map((item, i) => {
        if (!item.inactive)
          return (
            <IconButton
              buttonProps={{
                className: "cancelDrag !h-6 !w-6 !rounded",
                onClick: item.onClick,
                variant: "tertiary",
              }}
              iconProps={{
                color: item.color || configsIconsColor,
                iconName: item.iconName,
                stroke: item.stroke && configsIconsColor,
                svgClassName,
              }}
              key={i}
            />
          );
      })}
      <EmptyErrorBoundary>
        {dataToDownload?.filter((item) => item !== null && item !== undefined)?.length > 0 && (
          <CSVLink className="cancelDrag" color={configsIconsColor} data={dataToDownload} />
        )}
      </EmptyErrorBoundary>
      {captureTargetElement && (
        <CaptureComponent
          buttonProps={{ className: "cancelDrag" }}
          color={configsIconsColor}
          iconProps={{ svgClassName }}
          targetElement={captureTargetElement}
        />
      )}
    </Stack>
  );
};

const DynamicChartNavbarAndSidebar = forwardRef(function DynamicChartNavbarAndSidebarWithRef(props, ref) {
  const {
    captureTargetElement,
    categoricalFilter,
    chartType,
    diagrams,
    filters,
    filtersCategories,
    id,
    isConfigSetting,
    isFullScreen,
    isVisible,
    multithread,
    onClose,
    removeChartIndividalFilterKey,
    setChartSelectionIsOpen,
    setDiagrams,
    setFullScreenChartId,
    setIsConfigSetting,
    setIsVisible,
    settings,
    xCoord,
  } = props;

  const { t } = useTranslation();

  const [openSidebar, setOpenSidebar] = useState(false);
  const [section, setSection] = useState(null);
  const handleClick = (selectedSection) => {
    setSection(selectedSection);
    setOpenSidebar(true);
    // setIsConfigSetting(true)
  };

  useImperativeHandle(ref, () => ({
    childHandleClick: handleClick,
  }));

  const hasFilters = filters?.length > 0;
  return (
    <>
      <ChartNavbar
        captureTargetElement={captureTargetElement}
        enterFullScreenMode={() => setFullScreenChartId(id)}
        exitFullScreenMode={() => setFullScreenChartId(null)}
        handleClick={handleClick}
        hasFilters={hasFilters}
        id={id}
        isFullScreen={isFullScreen}
        isVisible={isVisible}
        onClose={() => onClose?.(id)}
        setChartSelectionIsOpen={setChartSelectionIsOpen}
        setIsVisible={setIsVisible}
      />
      {openSidebar && (
        <Modal
          disable
          onClose={() => {
            setOpenSidebar(false);
            // setIsConfigSetting(false)
          }}
          open={openSidebar}
          slotProps={{
            backdrop: {
              sx: {
                backgroundColor: "var(--color-overlay-scrim)",
              },
            },
          }}
        >
          <Paper
            className={`cancelDrag fixed bottom-8 top-8 z-[6] w-[60rem] overflow-y-auto rounded ${
              xCoord < 6 ? "right-8" : "left-8"
            }`}
            style={{
              backdropFilter: "blur(10px)",
              background: "rgba(255,255,255,0.6)",
            }}
          >
            {multithread && (
              <Stack className="mb-4 justify-between">
                <div className=" font-ds-headline-md">
                  {/* <span>{t('EnergyPerformance.dashboard.liveCalculationMode')}</span> */}
                  {/* <Switch checked={isLiveMode} onChange={(e) => setIsLiveMode(e.target.checked)} /> */}
                  <Button
                    disabled={isConfigSetting}
                    onClick={() => {
                      setIsConfigSetting(true);
                      // setIsLiveMode(false)
                    }}
                    size="md"
                    variant="error"
                  >
                    <Stack className="capitalize" gap={2}>
                      <Icon iconName="Pause" size="md" />
                      {t("EnergyPerformance.dashboard.pauseInstantCalculation")}
                    </Stack>
                  </Button>
                </div>
                <Button
                  disabled={!isConfigSetting}
                  onClick={() => {
                    setIsConfigSetting(false);
                    setOpenSidebar(false);
                    // setIsLiveMode(true)
                  }}
                  size="md"
                  variant="success"
                >
                  <Stack gap={2}>
                    <Icon iconName="Play" size="md" />
                    {t("general.run")}
                  </Stack>
                </Button>
              </Stack>
            )}
            {section === "settings" && (
              <DynamicChartSettings
                diagrams={diagrams}
                selectedChartType={chartType}
                setDiagrams={setDiagrams}
                settings={settings}
              />
            )}
            {section === "filters" && (
              <>
                {categoricalFilter ? (
                  <ChartCategoricalIndividualFilters
                    filters={filters}
                    filtersCategories={filtersCategories}
                    removeChartIndividalFilterKey={removeChartIndividalFilterKey}
                  />
                ) : (
                  <ChartIndividualFiltersModal filters={filters} />
                )}
              </>
            )}
          </Paper>
        </Modal>
      )}
    </>
  );
});

const DynamicChartBody = ({
  aggregationType,
  appName,
  chartType,
  config,
  dataPoint,
  dataTransformator,
  dataTransformatorMultithread,
  description,
  diagrams,
  eventHandlers,
  filters,
  generalData,
  getColor,
  getColumnDisplayName,
  getColumnSortFunction,
  getCustomAggregationFunction,
  getDataTransformatorMemoDependencyArray,
  getUnit,
  height,
  hoverEffect,
  id,
  IdColumnName,
  isConfigSetting,
  isError,
  isItemHovered,
  isLoading,
  legendsPosition,
  miniMapObjectsNameKey,
  multithread,
  onClick,
  onHover,
  onLegendSelect,
  onMouseOut,
  secondYAxisName,
  secondYAxisRange,
  settings,
  somethingIsHovered,
  sortValue,
  specificData,
  title,
  width,
  xAxis,
  xAxisName,
  xAxisRange,
  yAxis,
  yAxisName,
  yAxisRange,
}) => {
  const { t } = useTranslation();
  const data = specificData?.length ? specificData : generalData;

  const [filteredData, setFilteredData] = useState(data);

  const triggerFitleringDependency = JSON.stringify([
    filters?.filter((f) => f.mainDataKey)?.map((f) => f?.config[f?.filterName]),
    settings?.filter((s) => s.mainDataKey && s.useAsDataFilter)?.map((s) => s?.config[s?.filterName]),
  ]);

  const piechartDependency = JSON.stringify(config.chartType === chartTypesEnums.PIE && config);

  useEffect(() => {
    if (!isLoading && !isConfigSetting) {
      const unsortedData = [...data];
      let partiallyFilteredData = unsortedData;
      filters.forEach((filter) => {
        const { filterType, mainDataKey, ...args } = filter;
        if (!mainDataKey) return;
        const filterFactory = new DynamicFilterFactory(filterType, args);
        const filterInstance = filterFactory.createFilterClassInstance();
        partiallyFilteredData = filterInstance.filterData(partiallyFilteredData, mainDataKey);
      });
      settings.forEach((setting) => {
        const { filterType, mainDataKey, useAsDataFilter, ...args } = setting;
        if (!mainDataKey || !useAsDataFilter) return;
        const filterFactory = new DynamicFilterFactory(filterType, args);
        const filterInstance = filterFactory.createFilterClassInstance();
        partiallyFilteredData = filterInstance.filterData(partiallyFilteredData, mainDataKey);
      });

      setFilteredData(partiallyFilteredData);
    }
  }, [
    generalData,
    isLoading,
    specificData,
    triggerFitleringDependency,
    config?.w,
    config?.h,
    piechartDependency,
    isConfigSetting,
  ]);

  const aggregationFunction = getCustomAggregationFunction?.({ config }) ?? aggregationFunctionConfig[aggregationType];
  return (
    <div className="relative h-full">
      <LoadingOrEmptyWrapper
        height="100%"
        loadingText={t("general.loadingDataPleaseWait")}
        showEmpty={!data?.length || isError}
        showLoading={isLoading}
      >
        <ResponsiveContainer height="100%" width="100%">
          {/* <Profiler id={chartType + Math.random()} onRender={(id, phase, actualDuration) => {
          }}> */}
          <ChartType
            aggregationFunction={aggregationFunction}
            appName={appName}
            chartsConfigs={diagrams}
            chartType={chartType}
            config={config}
            data={filteredData}
            dataPoint={dataPoint}
            dataTransformator={dataTransformator}
            dataTransformatorMultithread={dataTransformatorMultithread}
            description={description}
            eventHandlers={eventHandlers}
            getColor={getColor}
            getColumnDisplayName={getColumnDisplayName}
            getColumnSortFunction={getColumnSortFunction}
            getDataTransformatorMemoDependencyArray={getDataTransformatorMemoDependencyArray}
            getUnit={getUnit}
            height={height}
            hoverEffect={hoverEffect}
            id={id}
            IdColumnName={IdColumnName}
            isConfigSetting={isConfigSetting}
            isItemHovered={isItemHovered}
            legendsPosition={legendsPosition}
            miniMapObjectsNameKey={miniMapObjectsNameKey}
            multithread={multithread}
            onClick={onClick}
            onHover={onHover}
            onLegendSelect={onLegendSelect}
            onMouseOut={onMouseOut}
            secondYAxisName={secondYAxisName}
            secondYAxisRange={secondYAxisRange}
            somethingIsHovered={somethingIsHovered}
            sort={sortValue}
            sortBy={xAxis === "index" ? sortValue : xAxis}
            title={title}
            width={width}
            xAxisName={xAxisName}
            xAxisRange={xAxisRange}
            xDataKey={xAxis}
            yAxisName={yAxisName}
            yAxisRange={yAxisRange}
            yDataKey={yAxis}
          />
          {/* </Profiler> */}
        </ResponsiveContainer>
      </LoadingOrEmptyWrapper>
    </div>
  );
};

const ChartTypeSelection = ({ chartOptions, handleChartSelection }) => {
  return (
    <Grid className="mt-1 h-full w-full" container justifyContent="space-evenly" spacing={2}>
      {chartOptions?.map((option, index) => {
        const IconComponent = option.icon;
        return (
          <Grid className="transition" item key={index} xs={3}>
            <Stack className="items-center justify-center">
              <Stack
                className="h-15 w-15 cursor-pointer rounded-md bg-lds-selection-subtle p-2 outline-none transition-all duration-300 hover:bg-lds-selection-strong hover:outline  hover:outline-ds-accent"
                direction="column"
                onClick={() => {
                  handleChartSelection(option.type);
                }}
              >
                <IconComponent color={option.color} size={32} />
                <span className="font-ds-label-sm">{option.label} </span>
              </Stack>
            </Stack>
          </Grid>
        );
      })}
    </Grid>
  );
};

// The userLanguage parameter is used only to make a rerender when changed.
// TODO: Use a better approach
const DynamicChartMemoized = ({
  appName,
  categoricalFilter,
  chartTypes,
  config,
  dataTransformator,
  dataTransformatorMultithread,
  eventHandlers,
  filters,
  filtersCategories,
  generalData,
  getColor,
  getColumnDisplayName,
  getColumnSortFunction,
  getCustomAggregationFunction,
  getDataTransformatorMemoDependencyArray,
  getUnit,
  height,
  hoverEffect,
  id,
  IdColumnName,
  isFullScreen,
  isItemHovered,
  miniMapObjectsNameKey,
  multithread,
  onClick,
  onClose,
  onHover,
  onLegendSelect,
  onMouseOut,
  removeChartIndividalFilterKey,
  setConfig,
  setFullScreenChartId,
  settings,
  somethingIsHovered,
  specificDataGetter,
  // eslint-disable-next-line no-unused-vars
  userLanguage,
  xCoord,
}) => {
  console.log(id);
  const {
    aggregationType,
    chartType,
    dataPoint,
    description,
    diagrams,
    legendsPosition,
    secondYAxisName,
    secondYAxisRange,
    sortValue,
    title,
    xAxis,
    xAxisName,
    xAxisRange,
    yAxis,
    yAxisName,
    yAxisRange,
  } = config;

  const setChartType = (newValue) => setConfig("chartType", newValue);

  const { isError, isLoading, specificData } = specificDataGetter(config);
  const [chartSelectionIsOpen, setChartSelectionIsOpen] = useState(!chartType);

  const navbarAndSidebarRef = useRef();
  const captureRef = useRef();
  const handleChartSelection = (type) => {
    setChartType(type);
    setChartSelectionIsOpen(false);
    navbarAndSidebarRef.current?.childHandleClick("settings");
  };

  const color = "var(--color-element-primary-initial)";
  const allAvailableChartOptions = [
    {
      color,
      icon: LiaChartAreaSolid,
      label: "Area",
      type: chartTypesEnums.AREA,
    },
    {
      color,
      icon: PiChartDonutThin,
      label: "Doughnut",
      type: chartTypesEnums.DOUGHNUT,
    },
    {
      color,
      icon: IoPieChartOutline,
      label: "Pie",
      type: chartTypesEnums.PIE,
    },
    {
      color,
      icon: GiHistogram,
      label: "Histogram",
      type: chartTypesEnums.HISTOGRAM,
    },
    {
      color,
      icon: TbChartHistogram,
      label: "Combo",
      type: chartTypesEnums.COMPOSED,
    },
    {
      color,
      icon: IoBarChartOutline,
      label: "Bar",
      type: chartTypesEnums.BAR,
    },
    {
      color,
      icon: PiChartLineLight,
      label: "Line",
      type: chartTypesEnums.LINE,
    },
    {
      color,
      icon: PiChartScatter,
      label: "Scatter",
      type: chartTypesEnums.SCATTER,
    },
    {
      color,
      icon: TbSum,
      label: "KPI",
      type: chartTypesEnums.KPI,
    },
    {
      color,
      icon: TfiLayoutGrid4Alt,
      label: "Heatmap",
      type: chartTypesEnums.HEATMAP,
    },
    {
      color,
      icon: FaMapLocationDot,
      label: "Map",
      type: chartTypesEnums.MINI_MAP,
    },
    {
      color,
      icon: BsFileText,
      label: "Summary",
      type: chartTypesEnums.SUMMARY,
    },
  ];

  const chartOptions = allAvailableChartOptions.filter(
    (chartOption) => !chartTypes || chartTypes?.includes(chartOption.type)
  );

  const [isVisible, setIsVisible] = useState(true);

  const setDiagrams = (newValue) => setConfig("diagrams", newValue);

  const [isConfigSetting, setIsConfigSetting] = useState(false);

  return (
    <>
      <Paper className="h-full p-4">
        <div className={`h-full w-full ${chartSelectionIsOpen && "bg-lds-surface-bright"}`}>
          <DynamicChartNavbarAndSidebar
            captureTargetElement={() => captureRef.current}
            categoricalFilter={categoricalFilter}
            chartType={chartType}
            diagrams={diagrams}
            filters={filters}
            filtersCategories={filtersCategories}
            id={id}
            isConfigSetting={isConfigSetting}
            isFullScreen={isFullScreen}
            isVisible={isVisible}
            multithread={multithread}
            onClose={onClose}
            ref={navbarAndSidebarRef}
            removeChartIndividalFilterKey={removeChartIndividalFilterKey}
            setChartSelectionIsOpen={setChartSelectionIsOpen}
            setDiagrams={setDiagrams}
            setFullScreenChartId={setFullScreenChartId}
            setIsConfigSetting={setIsConfigSetting}
            setIsVisible={setIsVisible}
            settings={settings}
            xCoord={xCoord}
          />
          <TextErrorBoundary>
            {isVisible && (
              <div className="cancelDrag" ref={captureRef} style={{ height: "calc(100% - 24px)" }}>
                {chartSelectionIsOpen && (
                  <ChartTypeSelection chartOptions={chartOptions} handleChartSelection={handleChartSelection} />
                )}
                {!chartSelectionIsOpen && !isConfigSetting && (
                  <DynamicChartBody
                    aggregationType={aggregationType}
                    appName={appName}
                    chartType={chartType}
                    config={config}
                    dataPoint={dataPoint}
                    dataTransformator={dataTransformator}
                    dataTransformatorMultithread={dataTransformatorMultithread}
                    description={description}
                    diagrams={diagrams}
                    eventHandlers={eventHandlers}
                    filters={filters}
                    generalData={generalData}
                    getColor={getColor}
                    getColumnDisplayName={getColumnDisplayName}
                    getColumnSortFunction={getColumnSortFunction}
                    getCustomAggregationFunction={getCustomAggregationFunction}
                    getDataTransformatorMemoDependencyArray={getDataTransformatorMemoDependencyArray}
                    getUnit={getUnit}
                    height={height}
                    hoverEffect={hoverEffect}
                    id={id}
                    IdColumnName={IdColumnName}
                    // isConfigSetting={isConfigSetting && !isLiveMode}
                    isConfigSetting={isConfigSetting}
                    isError={isError}
                    isItemHovered={isItemHovered}
                    isLoading={isLoading}
                    legendsPosition={legendsPosition}
                    miniMapObjectsNameKey={miniMapObjectsNameKey}
                    multithread={multithread}
                    onClick={onClick}
                    onHover={onHover}
                    onLegendSelect={onLegendSelect}
                    onMouseOut={onMouseOut}
                    secondYAxisName={secondYAxisName}
                    secondYAxisRange={secondYAxisRange}
                    settings={settings}
                    somethingIsHovered={somethingIsHovered}
                    sortValue={sortValue}
                    specificData={specificData}
                    title={title}
                    width="100%"
                    xAxis={xAxis}
                    xAxisName={xAxisName}
                    xAxisRange={xAxisRange}
                    yAxis={yAxis}
                    yAxisName={yAxisName}
                    yAxisRange={yAxisRange}
                  />
                )}
              </div>
            )}
          </TextErrorBoundary>
        </div>
      </Paper>
    </>
  );
};

export default React.memo(DynamicChartMemoized, isEqual);
